import { TICK_RATE, ticks } from './config';

/** Sentinel for "never runs out" (the default gun's reserve, and every crate gun's since ADR-0025). */
export const INFINITE_RESERVE = 0xffff;

/**
 * How long a picked-up gun lasts (ADR-0025): reloads are free, the clock is the limit — when it runs out the holder is
 * back on the default gun. Taking the same gun again restarts it.
 */
export const GUN_TIME_TICKS = ticks(30);

/** How long a picked-up gun lasts, ticks: its own `time` (the Mecha Suit's shorter clock, ADR-0096), else GUN_TIME_TICKS. */
export const gunTicks = (w: { readonly time?: number }): number => w.time ?? GUN_TIME_TICKS;

/**
 * Weapon tuning. `sim`-relevant fields drive the simulation (shared with the client predictor);
 * `feel` is read only by the client's juice layer. Drop rarity lives in `economy.ts` (LOOT).
 *
 * Balance (see docs/adr/0008, 0025): every crate gun beats the Pistol its own way, and every one drops as often, but
 * only for a while — a crate gun reloads for free for GUN_TIME_TICKS (30 s), then reverts to the Pollinator. The atom
 * bomb stays one use. Gun against gun is measured in the duel lab (ADR-0040): `bun run duel`.
 *
 * | weapon (CONSTANT)          | key        | sprite      | dmg × rps    | DPS | mag     | notes                      |
 * |----------------------------|------------|-------------|--------------|-----|---------|----------------------------|
 * | Pistol (POLLINATOR)        | pollinator | pollinator  |  8 × 7.5     |  60 | 24      | default, forgiving, never  |
 * |                            |            |             |              |     |         | runs out                   |
 * | Pistol (ROYALE_PISTOL)     | pollinator | pollinator  |  8 × 6       |  48 | 12      | a battle royale's (ADR-    |
 * |                            |            |             |              |     |         | 0189): everyone's at first |
 * | Rifle (THORNFIRE)          | thornfire  | thornfire   | 10 × 10      | 100 | 30      | tighter, faster, longer    |
 * |                            |            |             |              |     |         | range                      |
 * | Flamethrower (WEED_BURNER) | burner     | weed_burner | 2×5 × 15     | 150 | 60      | 135 px cone, flames pierce |
 * |                            |            |             |              |     |         | 2 and have a body; burns   |
 * |                            |            |             |              |     |         | 3 × 3/s for 2 s after      |
 * | Sniper (LONGSTEM)          | longstem   | longstem    | 55 × 1.1     |  61 | 5       | one-shots minions, pierces |
 * |                            |            |             |              |     |         | 3 (none in a battle        |
 * |                            |            |             |              |     |         | royale), 560 px; a scope:  |
 * |                            |            |             |              |     |         | a longer, narrower cone    |
 * | Sniper (ROYALE_SNIPER)     | longstem   | longstem    | 55 × 0.67    |  37 | 1       | a battle royale's: a 1.2 s |
 * |                            |            |             |              |     |         | reload after every shot    |
 * | Minigun (WHACKER)          | whacker    | whacker     |  6 × 30      | 180 | 150     | 0.4 s spin-up, 55% move    |
 * |                            |            |             |              |     |         | speed firing               |
 * | Minigun (ROYALE_MINIGUN)   | whacker    | whacker     |  6 × 30      | 180 | 100     | a battle royale's: 0.6 s   |
 * |                            |            |             |              |     |         | spin-up, 4 s reload        |
 * | Laser (SUNBEAM)            | laser      | laser_gun   |  3 × 30      |  90 | 90      | continuous beam, hits      |
 * |                            |            |             |              |     |         | instantly, 300 px          |
 * | Bazooka (ROOT_ROCKET)      | rocket     | root_rocket | 70+55 splash |   — | 2       | fast seeking rocket, 50 px |
 * |                            |            |             |              |     |         | blast                      |
 * | Atom Bomb (ATOM_BOMB)      | nuke       | nuke_remote | 600 → 150    |   — | 1, once | calls a bomb on a spot you |
 * |                            |            |             |              |     |         | pick (NUKE); a base in the |
 * |                            |            |             |              |     |         | blast loses a third of its |
 * |                            |            |             |              |     |         | health; one a round        |
 * | Freeze Gun (FROSTBITE)     | freeze     | frostbite   |  7 × 7.5     |  52 | 24      | every hit slows the target |
 * |                            |            |             |              |     |         | for 2 s (FROST)            |
 * | Grenade Launcher           | grenade    | cone_lobber | 65 splash    |   — | 4       | pinecone grenade, bursts   |
 * | (CONE_LOBBER)              |            |             |              |     |         | near an enemy              |
 * | Railgun (CORN_RAIL)        | rail       | corn_rail   | 18→90        |   — | 4       | hold to charge 1.3 s, let  |
 * |                            |            |             |              |     |         | go: an instant rail; full  |
 * |                            |            |             |              |     |         | charge pierces everything, |
 * |                            |            |             |              |     |         | kicks back, burns a tunnel |
 * |                            |            |             |              |     |         | through a hedge            |
 * | Katana (PRUNER)            | katana     | katana      | 40 a slash   |   — | 3       | click: dash 78 px cutting |
 * |                            |            |             |              |     |         | everything on the way; a   |
 * |                            |            |             |              |     |         | kill gives a charge back;  |
 * |                            |            |             |              |     |         | a 100 HP guard while you   |
 * |                            |            |             |              |     |         | hold it                    |
 * | Robo Fist (VINE_FIST)      | fist       | robo_fist   | 8 + 42 punch |   — | 3       | a hook yanks what it       |
 * |                            |            |             |              |     |         | catches to you, then the   |
 * |                            |            |             |              |     |         | fist punches it away along |
 * |                            |            |             |              |     |         | your aim                   |
 * | Homing Gun (FIREFLY)       | firefly    | firefly_gun |  7 × 10      |  70 | 24      | fireflies bend hard onto   |
 * |                            |            |             |              |     |         | what they can see, round   |
 * |                            |            |             |              |     |         | corners too                |
 * | Homing Gun (ROYALE_HOMING) | firefly    | firefly_gun |  6 × 10      |  60 | 24      | a battle royale's: bend    |
 * |                            |            |             |              |     |         | only onto a near miss      |
 * | Hive Launcher (BEEHIVE)    | hive       | hive_launch |  5 × 10 bees |   — | 3       | a lobbed hive bursts into  |
 * |                            |            | er          |              |     |         | bees that hunt, fly over   |
 * |                            |            |             |              |     |         | hedges and sting once      |
 * | Buzz Saw (BUZZSAW)         | saw        | buzz_saw    |  7 × 15      | 105 | 150     | melee: grinds what the     |
 * |                            |            |             |              |     | (fuel)  | blade touches; its front   |
 * |                            |            |             |              |     |         | parries bullets for fuel   |
 * | Shurikens (SHURIKEN)       | shuriken   | shuriken_   | 3×8 × 5      | 120 | 20      | a fan of three stars: all  |
 * |                            |            | fan         |              |     |         | land up close, few at range|
 * | Toxic Launcher             | toxic      | toxic_      | 28 + 8 × 4/s |   — | 4       | a canister bursts near an  |
 * | (SLUDGE)                   |            | launcher    |              |     |         | enemy into a puddle that   |
 * |                            |            |             |              |     |         | hurts what stands in it    |
 * | Cupid's Bow (CUPID_BOW)    | cupid      | cupid_bow   | 16→80        |   — | 5       | draw 1 s, loose an arrow;  |
 * |                            |            |             |              |     |         | an enemy minion it hits    |
 * |                            |            |             |              |     |         | falls in love: yours now   |
 * | Dubstep Gun (DUBSTEP)      | dubstep    | dubstep_gun | 12 × 6       |  72 | 36      | the drop (0.35 s), then    |
 * |                            |            |             |              |     |         | wide sound waves through 4 |
 * | Firework Launcher          | firework   | firework_   | 5 × 24 splash|   — | 3       | a fan of five rockets that |
 * | (FIREWORK)                 |            | launcher    |              |     |         | burst at scattered ranges  |
 * | Mecha Suit (MECHA)         | mecha      | mecha_guns  |  8 × 15      | 120 | 80      | a walking robot: two       |
 * |                            |            |             |              |     |         | miniguns, a 110 HP guard,  |
 * |                            |            |             |              |     |         | 0.4× your pace; right      |
 * |                            |            |             |              |     |         | click: 2 rockets (30+28)   |
 * |                            |            |             |              |     |         | every 7 s; lasts 22.5 s    |
 * | Demon Grimoire (GRIMOIRE)  | demon      | grimoire    | a demon      |   — | 1, once | a blood pentagram round    |
 * |                            |            |             |              |     |         | you; a demon rises from it |
 * |                            |            |             |              |     |         | (DEMON) and marches on     |
 * | Magic Wand (WAND)          | wand       | magic_wand  |  8 × 10 bolt |  80 | 45      | lightning falls from the   |
 * |                            |            |             |  26 px blast |     |         | sky on a storm that trails |
 * |                            |            |             |              |     |         | the crosshair (110 px/s)   |
 * | Alien Attack               | alien      | alien_remote| 28 a pulse   |   — | 1, once | a mothership burns a laser |
 * | (ALIEN_ATTACK)             |            |             |              |     |         | along a path you draw (UFO)|
 * | RC Tractor (RC_TRACTOR)    | rc         | rc_remote   |  7 × 6 + 90  |   — | 1, once | a toy tractor you steer    |
 * |                            |            |             |  blast       |     |         | with the crosshair, faster |
 * |                            |            |             |              |     |         | and faster, shooting       |
 * |                            |            |             |              |     |         | ahead; it blows up on what |
 * |                            |            |             |              |     |         | it meets; you stand        |
 * |                            |            |             |              |     |         | shielded at the controls   |
 * | Monster Tonic (MONSTER)    | monster    | monster_    | 46 slam,     |   — | 1       | you turn into a red        |
 * |                            |            | tonic       | 40 boulder   |     | (leap)  | monster: a 140 HP guard,   |
 * |                            |            |             |              |     |         | 1.15× pace; leap onto the  |
 * |                            |            |             |              |     |         | crosshair and crush; right |
 * |                            |            |             |              |     |         | click: a boulder that rolls|
 * |                            |            |             |              |     |         | through all in its way     |
 * | Fire Bow (FIRE_BOW)        | firebow    | fire_bow    | 30 splash +  |   — | 4       | draw 1 s: a flaming arrow  |
 * |                            |            |             | fire 6/s     |     |         | lobbed over hedges onto the|
 * |                            |            |             |              |     |         | crosshair; a patch of fire |
 * | Shield (SHIELD)            | shield     | shield      | 24 a bounce  |   — | 3       | right: hold it up, 3 blows |
 * |                            |            |             |              |     | (blocks)| then it breaks (7 s); left:|
 * |                            |            |             |              |     |         | seeks, 8 bounces, back     |
 * | Air Strike (AIR_STRIKE)    | airstrike  | airstrike_  | 8 × 60→30    |   — | 1, once | a jet ripples rockets down |
 * |                            |            | radio       | blasts       |     |         | a path you draw (JETS)     |
 * | Lightsaber (LIGHTSABER)    | saber      | lightsaber  | 30 a swing   | 100 | INF     | swinging, it cuts shots out|
 * |                            |            |             |              |     |         | of the air from the front; |
 * |                            |            |             |              |     |         | right: thrown, spins       |
 * |                            |            |             |              |     |         | through 3 (40), every 3 s  |
 * | Death Scythe (SCYTHE)      | scythe     | scythe      | 48 a sweep   | 103 | 8       | a wide sweep; each hit     |
 * |                            |            |             |              |     |         | heals you 10; under 25%    |
 * |                            |            |             |              |     |         | health it kills outright   |
 * | Giant Hammer (HAMMER)      | hammer     | giant_hammer| 70 → 35 slam |   — | INF     | raise 0.5 s, slam 26 px out|
 * |                            |            |             |              |     |         | over 52 px: it throws      |
 * | Neptune's Trident          | trident    | trident     | 34 a wave    |   — | 3       | one great wave, 36 px wide,|
 * | (TRIDENT)                  |            |             |              |     |         | through all, carries them  |
 * | Tank (TANK)                | tank       | tank_cannon | 75+60 splash |   — | 4       | you drive a tank: a 150    |
 * |                            |            |             |              |     |         | guard, 0.7× pace; right:   |
 * |                            |            |             |              |     |         | a guided missile (50+45)   |
 * |                            |            |             |              |     |         | every 5 s; 25 s            |
 * | Unicorn (UNICORN)          | unicorn    | unicorn_horn|  4 × 30      | 120 | 90      | you are a unicorn: 1.3×    |
 * |                            |            |             |              |     |         | pace, a rainbow beam; right|
 * |                            |            |             |              |     |         | a fart: choking gas 4 s    |
 * | Duck Family (DUCKS)        | ducks      | duck_whistle| 70 + 4 × 40  |   — | 1, once | a duck and four ducklings  |
 * |                            |            |             | blasts       |     |         | hunt and blow up; shootable|
 *
 * Players see the plain names (ADR-0026). The code names are a mix: constants keep the garden names, `key` (what
 * `START_WEAPON` and the client's `WEAPON_FX` take) is a garden name for only some guns, and the held sprite
 * (`WEAPON_FX[key].sprite`, assets/src) is a third spelling; look them up here.
 * Only the Pistol (Pollinator) is carried by default; everything else comes out of airdrop crates (ADR-0010).
 * Player bullets are fast (ADR-0014: ~1.6× the old speeds) so a strafing player can be hit without guessing a
 * long lead; turret shots and boss seeds stay slow — dodging those is the point.
 * Turret weapons (cannon, boss seeds) are fired by non-player shooters through the same projectile path.
 */
export interface WeaponDef {
  readonly id: number;
  readonly key:
    | 'pollinator'
    | 'thornfire'
    | 'longstem'
    | 'whacker'
    | 'burner'
    | 'rocket'
    | 'laser'
    | 'nuke'
    | 'spore'
    | 'freeze'
    | 'grenade'
    | 'rail'
    | 'katana'
    | 'fist'
    | 'firefly'
    | 'hive'
    | 'saw'
    | 'shuriken'
    | 'toxic'
    | 'shotgun'
    | 'tornado'
    | 'abduct'
    | 'sword'
    | 'dart'
    | 'cupid'
    | 'dubstep'
    | 'firework'
    | 'mecha'
    | 'demon'
    | 'wand'
    | 'alien'
    | 'rc'
    | 'tractorGun'
    | 'monster'
    | 'boulder'
    | 'mechRocket'
    | 'cannon'
    | 'seed'
    | 'missile'
    | 'thorn'
    | 'spine'
    | 'acid'
    | 'bolt'
    | 'gourd'
    | 'meteor'
    | 'firebow'
    | 'shield'
    | 'frost'
    | 'airstrike'
    | 'saber'
    | 'scythe'
    | 'hammer'
    | 'trident'
    | 'tank'
    | 'tankMissile'
    | 'unicorn'
    | 'ducks'
    | 'duckling'
    | 'saberThrow'
    | 'gas'
    | 'sprinkler'
    | 'hivebee'
    | 'sunray'
    | 'mortar'
    | 'lava'
    | 'chomper'
    | 'handGrenade'
    | 'c4'
    | 'ejector'
    | 'house';
  readonly name: string;
  /** Rarity tier (0 = default gun): the pickup's colour and how much bots prize it; every gun drops as often (LOOT). */
  readonly rarity: number;
  /** Fired by a turret or a boss, never held by a player. */
  readonly turret: boolean;
  /** Fired by a gun it is mounted on (its `alt`), never held on its own: a player's shot all the same (ADR-0096). */
  readonly mount?: boolean;
  /**
   * No projectile (a Zombie Nights tower's, ADR-0171): its `shot` only marks what the tower did (a Sprinkler's spray, a bee
   * let out of a hive); the tower's own system dealt with it.
   */
  readonly mark?: true;
  /** Bullets per trigger pull, fanned evenly across `spread` (shotguns). */
  readonly pellets: number;
  /** Blast radius when the projectile ends (0 = no blast), px, and the blast's damage at its centre. */
  readonly splashRadius: number;
  readonly splashDamage: number;
  readonly fireIntervalTicks: number;
  readonly magazine: number;
  /** Spare rounds carried on pickup (INFINITE_RESERVE for every player gun but the one-use atom bomb). */
  readonly reserve: number;
  readonly reloadTicks: number;
  readonly damage: number;
  /** px/s */
  readonly projectileSpeed: number;
  /** Total cone width in radians. */
  readonly spread: number;
  readonly rangePx: number;
  /** Extra targets a bullet passes through after the first. */
  readonly pierce: number;
  /** `pierce` in a battle royale, where it differs (`pierceOf`): the Sniper stops at the first gardener it hits. */
  readonly royalePierce?: number;
  /** A scope (the Sniper): its holder sees through content `SCOPE`, a longer and narrower cone (`eyeOf`). */
  readonly scope?: true;
  /** Ticks the trigger must be held before the first shot (minigun). 0 = instant. */
  readonly spinUpTicks: number;
  /** Movement speed multiplier while the trigger is held / barrel spinning. */
  readonly firingMoveMultiplier: number;
  /** Ground-plane distance from the holder's feet to the muzzle. */
  readonly muzzleDistance: number;
  /**
   * Homing (turrets only): the projectile locks on to the turret's target when fired and steers onto it every
   * tick, flying over walls and past everything else — it only ever hits that target.
   */
  readonly homing?: boolean;
  /**
   * A continuous beam (laser): fired every tick, each shot crosses its whole range within one tick, so it lands
   * as good as instantly; the client draws it as a beam, not a bullet. No aim assist.
   */
  readonly beam?: boolean;
  /** Flames (flamethrower): drawn as fire puffs that grow and fade along their short flight. No aim assist. */
  readonly flame?: boolean;
  /** Seeks targets hard (rocket, `AIM_ASSIST[level].seek`) when a human fires it (ADR-0019). */
  readonly seek?: boolean;
  /** Its blast hurts and throws whoever fired it too (ADR-0194 amended: the Bazooka, a grenade); their mates stay spared. */
  readonly selfBlast?: boolean;
  /**
   * Never fires: holding it and clicking picks a spot on the (zoomed-out) map to call a strike on (`strike` message): the
   * atom bomb (NUKE, ADR-0019) or the alien mothership's laser (UFO, ADR-0099).
   */
  readonly strike?: StrikeDef;
  /**
   * Frosts what it hits (Frostbite): a hit sets the target's frost (`Frozen`) to this many ticks — never more, so hits
   * keep a target frosted but don't stack beyond it. Frosted things move at `FROST.speed` of their pace and still attack
   * (ADR-0031; ADR-0021 froze them solid).
   */
  readonly freezeTicks?: number;
  /**
   * Proximity fuse (Cone Lobber): the projectile bursts as soon as a hostile target's edge is within this many px
   * of it (and at walls and the end of its flight), dealing only its splash. No aim assist.
   */
  readonly fuse?: number;
  /**
   * A charged shot (Railgun): holding the trigger charges it (`Gun.spin`, up to `ticks`), letting go fires it — if it
   * charged at least `min` ticks — for `damage` × charge / `ticks`, piercing `pierce` × that many targets (a full charge:
   * everything). Charging slows you like firing (`firingMoveMultiplier`); a full charge can be held.
   */
  readonly charge?: { readonly ticks: number; readonly min: number; readonly pierce: number };
  /**
   * A full charge tunnels (Railgun, ADR-0038): it burns through the first hedge or thicket in its line (not trees,
   * rocks, crates or the map's rim), every cell of that structure within `radius` px of its line turning to scorched
   * earth you can walk through, and flies on to the next wall.
   */
  readonly tunnel?: { readonly radius: number };
  /** Kicks the shooter back at this many px/s when it fires (× the charge for a charged shot). Predicted. */
  readonly shove?: number;
  /** What a hit does to the body it hits (players, minions, creeps — not bosses): a knock (× the charge if charged). */
  readonly knock?: KnockDef;
  /** The projectile's own size (px): added to targets' hurt radius when it sweeps (the Robo Fist's claw). */
  readonly size?: number;
  /**
   * A dash-slash (Katana): firing dashes the holder along the aim for `ticks` at `speed` px/s (steering ignored; tiles
   * still stop it), cutting every hostile thing within `reach` px of its path once. A kill gives a charge back. No
   * projectile: the `shot` only marks the slash.
   */
  readonly dash?: { readonly ticks: number; readonly speed: number; readonly reach: number };
  /**
   * A guard (Katana, ADR-0035): taking the gun gives its holder this much shield (`Gun.guard`), soaking hits after an
   * Leaf Shield's; it is gone with the gun (another gun taken, its time up, death) and a fresh one comes with it.
   */
  readonly guard?: number;
  /** Never spends a round or reloads (Katana, Berserker Sword): only the fire interval paces it. The HUD counts INF. */
  readonly endless?: true;
  /**
   * A hook (Robo Fist): what it catches — a player, minion or creep — is dragged to the shooter at `pullSpeed` px/s
   * (landing `gap` px from them); as it arrives the fist punches it — always, wherever the shooter went meanwhile
   * (ADR-0039) — for `punchDamage`, launches it along the shooter's aim (`punch`) and dazes it for `stun` ticks: it can't
   * move, shoot or attack. Bosses and dummies are too heavy to reel in or daze: the punch lands where they stand.
   */
  readonly hook?: {
    readonly pullSpeed: number;
    readonly gap: number;
    readonly punchDamage: number;
    readonly punch: KnockDef;
    readonly stun: number;
  };
  /**
   * Bends hard onto a target (the Homing Gun, ADR-0078): every tick the bullet picks the hostile thing it points at most
   * nearly among those within `range` px, inside `cone` radians either side of its heading and in its line of sight (so it
   * flies straight past a hedge's corner, then turns in behind it), and turns towards it by at most `turnPerPx` radians
   * per px flown — never harder than the arc through the target. Anyone's shots, bots' too: it replaces the aim help.
   */
  readonly bend?: { readonly turnPerPx: number; readonly range: number; readonly cone: number };
  /** Bursts into `bees` angry bees where it ends (the Hive Launcher, ADR-0078; the bees are BEE). */
  readonly swarm?: { readonly bees: number };
  /**
   * A saw blade (the Buzz Saw, ADR-0078): no projectile. Every shot grinds every hostile thing within `radius` px (plus
   * its hurt radius) of the blade's centre — the muzzle — for `damage`. While it spins, player shots that meet its holder
   * from within `parry` radians of their aim are parried (stopped, no damage), each costing `parryCost` fuel (ammo) per
   * point of damage it would have done.
   */
  readonly saw?: { readonly radius: number; readonly parry: number; readonly parryCost: number };
  /**
   * A toxic puddle (the Toxic Launcher): wherever the shot ends it spills a puddle of `radius` px that lasts `ticks`. It
   * splashes `impact` onto every hostile gardener, minion, creep, boss or dummy in it at once, then bites everything
   * standing in it for `damage` every `every` ticks (puddles don't stack: one bite a pulse, however many overlap).
   */
  readonly puddle?: {
    readonly radius: number;
    readonly ticks: number;
    readonly impact: number;
    readonly damage: number;
    readonly every: number;
  };
  /**
   * Two guns fired in turn (the Mecha): each shot leaves from `dual` px to one side of the aim line, the next
   * from the other (by the magazine's count, so the predictor agrees).
   */
  readonly dual?: number;
  /**
   * A whirlwind (the Twister): it throws what it hits (`knock`) out to the side of its path it was on, half ahead,
   * rather than along it, and cuts each thing once.
   */
  readonly whirl?: boolean;
  /**
   * Damage over time (ADR-0083): a hit sets a gardener, minion, creep, boss or dummy on fire (the Flamethrower) or
   * poisons it (Poison Darts) for `ticks` more ticks (topped up by every hit), biting it for `damage` every `every` ticks.
   * A fire never stacks: one at a time, the latest. A `stack`ing poison gains a stack with every hit of its team, up to
   * `stack` of them, each stack biting for `damage` (a `poison` event says how many), and it burns beside a fire: neither
   * puts the other out.
   */
  readonly burn?: { readonly ticks: number; readonly damage: number; readonly every: number; readonly stack?: number };
  /**
   * A flying saucer comes for what it hits (Alien Abduction, ADR-0083): a gardener, minion or creep (not a boss, not a
   * dummy) loses `share` of its health (it never kills), hangs in the saucer's beam for `hold` ticks — dazed, and nothing
   * can hurt it — and is dropped `near`–`far` px away, somewhere it can stand. A hit leaves the gun `reload` ticks to
   * recharge; a miss only its short `reloadTicks`. The shot does no damage of its own.
   */
  readonly abduct?: {
    readonly hold: number;
    readonly share: number;
    readonly reload: number;
    readonly near: number;
    readonly far: number;
  };
  readonly charm?: boolean;
  readonly wave?: boolean;
  readonly stagger?: number;
  /**
   * Bounces off a wall once (the Dubstep Gun's waves, ADR-0096): a shot that meets a wall before it has hit anything flies
   * on mirrored off it, with what is left of its range; one that has hit something, or bounced already, ends there.
   */
  readonly bounce?: boolean;
  /**
   * Your pace while you hold it, firing or not (the Mecha Suit, ADR-0096); `firingMoveMultiplier` comes on top. Predicted.
   */
  readonly walk?: number;
  /**
   * A transformation (the Mecha Suit, the Monster Tonic; a boss's reward since ADR-0108, never in a crate): worn over the
   * gun you hold, which comes back as it was when it ends (`transform`, `Gun.under`). No gun can be taken while it lasts.
   */
  readonly transform?: boolean;
  /**
   * A summoning (the Demon Grimoire, ADR-0096): no projectile. Firing draws a blood pentagram of `radius` px beside the
   * holder on the side they face (its edge 8 px off their feet, nearer when a wall is in the way); `ticks` later a demon (content `DEMON`) rises from its middle for the holder's team and marches on the
   * nearest enemy base. Opening it costs the holder `blood` HP (never the last of it): a `hit` from nobody.
   */
  readonly summon?: { readonly ticks: number; readonly radius: number; readonly blood: number };
  /**
   * A second fire on the right mouse button (`Button.Alt`; the Mecha Suit's rockets, ADR-0096): `shots` of `weapon` (a
   * `mount`) at once, from either side, then `cooldown` ticks before the next; the gun's own magazine, reload and
   * cooldown are untouched. Predicted (`Gun.alt`). With a `windup` (the Monster Tonic's boulder, ADR-0102) the press
   * starts it (a `heave`) and the shots leave that many ticks later, the cooldown counted from the press.
   */
  readonly alt?: {
    readonly weapon: number;
    readonly shots: number;
    readonly cooldown: number;
    readonly windup?: number;
    /** Fired out behind its holder, not along the aim (the Unicorn's gas, ADR-0159). */
    readonly behind?: boolean;
    /**
     * The gun itself goes with it (the Lightsaber thrown, ADR-0159): for this many ticks after the press nothing is in hand
     * to fire with.
     */
    readonly away?: number;
  };
  /**
   * A storm (the Magic Wand, ADR-0097): no projectile. A storm point (`Gun.stormX/Y`) chases the crosshair (the aim at
   * the input's `reach`, never past `rangePx`) at `follow` px/s; every shot is a bolt of lightning from the sky within
   * `scatter` px of it, hurting every hostile thing within `radius` px (plus its hurt radius) for `damage`, walls or not;
   * a roof shelters (ADR-0189 amended: nobody in a house the holder isn't in).
   */
  readonly storm?: { readonly follow: number; readonly radius: number; readonly scatter: number };
  /**
   * An RC tractor (ADR-0101): no projectile. Firing puts a toy tractor down at the muzzle that drives itself — towards the
   * crosshair (the aim at the input's `reach`), turning at most `turn` radians a second — starting at `speed` px/s and
   * gaining `accel` px/s every second, to `maxSpeed`. Every `every` ticks it fires `gun` (a `mount`) straight ahead. It has
   * `hp` health; whatever it meets — a wall, water, a tree, a crate, anything hostile within `radius` px — or `ticks`
   * after it set off, or shot down, it blows up: `blast` damage at the centre over `blastRadius` px. Its driver stands at
   * the controls the while — dazed (no walking, no shooting: predicted from the shot, `Stun`) and shielded from all harm.
   */
  readonly tractor?: {
    readonly hp: number;
    readonly speed: number;
    readonly accel: number;
    readonly maxSpeed: number;
    readonly turn: number;
    readonly ticks: number;
    readonly gun?: number;
    readonly every: number;
    readonly radius: number;
    readonly blast: number;
    readonly blastRadius: number;
    /**
     * Drives itself (the Duck Family, ADR-0159): no driver at the controls — it heads for the nearest hostile thing it can
     * see within `hunt` px, else waddles straight on, and turns aside at walls and water rather than blowing up there.
     */
    readonly hunt?: number;
    /** Set down with `count` more behind it in a line, of `weapon` (a `mount`'s own `tractor`): a duck's ducklings. */
    readonly brood?: { readonly weapon: number; readonly count: number };
  };
  /**
   * A leap (the Monster Tonic, ADR-0102): no projectile. Firing throws its holder through the air towards the crosshair
   * (the aim at the input's `reach`, at least `min` px and at most `rangePx`) over `ticks` ticks — a Katana's dash
   * underneath (predicted), flying over walls onto the clear ground of its line nearest the crosshair (`leapLanding`) — and
   * where they land they crush everything hostile within `radius`
   * px (edge distance) for `damage`, throwing it out of the ring (`knock`, from the landing spot).
   */
  readonly leap?: {
    readonly ticks: number;
    readonly min: number;
    readonly radius: number;
    readonly damage: number;
    readonly knock: KnockDef;
  };
  /**
   * A lob (the Fire Bow, ADR-0109): the shot flies over everything — hedges, bodies — and comes down where the crosshair is
   * (the aim at the input's `reach`), no farther than the draw lets it: `min` px for the weakest draw, `rangePx` for a full
   * one (`charge`). It ends there (`range`), bursting like any other shot. Its `power` on the wire is its flight in
   * `LOB_STEP`s (`lobFlight`), not the charge.
   */
  readonly lob?: { readonly min: number };
  /**
   * A shield (ADR-0109). Held up on the right mouse button (`Button.Alt`) it blocks every hit that comes at its holder
   * from within `arc` radians of their aim, at `walk` × their pace; each blow it stops costs a charge (a round of the
   * magazine; more hits within `grace` ticks of a costly one are the same blow), and with the last one it breaks and
   * grows back over `reloadTicks`. The left button throws it towards the nearest visible enemy player within `rangePx`
   * (else another hostile target, else the aim): it flies at `projectileSpeed`, hurting what it meets for
   * `damage` (and `knock`), glancing off walls and off hostile things `bounces` times — off a hostile thing on to the
   * nearest other within `seek` px it can see, steering onto it until they meet — then flies home over everything,
   * `home` × as fast, and is back in its holder's hands; nothing left to bounce to, or `rangePx` without a bounce, turns
   * it home too. Away at most `away` ticks. While it is away, or broken, there is no shield to hold up or throw.
   */
  readonly shield?: {
    readonly arc: number;
    readonly grace: number;
    readonly walk: number;
    readonly bounces: number;
    readonly seek: number;
    readonly away: number;
    readonly home: number;
  };
  /**
   * A stomp (the Giant Hammer, ADR-0159): no projectile. The trigger raises it (`spinUpTicks`, from nothing every slam) and
   * it comes down at the muzzle — short of a wall in between — in a blast of `damage` (half at the rim) over `radius` px that
   * throws everything out of it (`knock`), like the Pumpkin King's slam.
   */
  readonly stomp?: { readonly radius: number; readonly knock: KnockDef };
  /** Each hit heals its holder this much (the Death Scythe, ADR-0159), never past their full health. */
  readonly reap?: number;
  /**
   * Death's touch (the Death Scythe, ADR-0159): a cut on a gardener, minion, creep or zombie left with at most this share of
   * its health kills it outright (never a boss, a base or a build).
   */
  readonly execute?: number;
  /** Hurt radius of its holder (a Mecha Suit is a bigger target than a gardener, ADR-0096). */
  readonly hurt?: number;
  /** How long a picked-up one lasts, ticks, when not GUN_TIME_TICKS (the Mecha Suit's shorter clock, ADR-0096). */
  readonly time?: number;
  readonly feel: {
    /** Trauma added to the camera per shot (0..1). */
    readonly shake: number;
    /** Camera kick opposite to the aim, px. */
    readonly kick: number;
    /** Gun sprite recoil, px. */
    readonly recoil: number;
    /** Crosshair bloom per shot, px. */
    readonly bloom: number;
    /** Rarity colour for pickups/HUD (hex). */
    readonly color: number;
  };
}

/**
 * A knock (ADR-0035): the body is thrown at `speed` px/s for `ticks` ticks, slowing by `decay` every tick (1 = a steady
 * drag). It can't steer meanwhile. A `slam` knock that throws it into a wall at `KNOCK.slamSpeed` or more stops it
 * dead there and hurts it (`KNOCK.slamDamage`, SPLAT).
 */
export interface KnockDef {
  readonly speed: number;
  readonly ticks: number;
  readonly decay: number;
  readonly slam: boolean;
}

/** Wall slams (ADR-0035): a slamming knock into a wall at this many px/s or more hurts for `slamDamage`. */
export const KNOCK = { slamSpeed: 110, slamDamage: 22 } as const;

/**
 * Blasts throw bodies (ADR-0036): a rocket's or grenade's blast knocks every hostile body it reaches — the one it hit
 * head-on too — straight away from its centre: this knock at the centre, half of it at the rim (measured to the body's
 * edge, like the damage). Walls shelter from it like from the damage; bosses are too heavy. It slams, so a body blown
 * into a hedge takes `KNOCK.slamDamage` (SPLAT). `speed` 0 turns it off (arena A/B: `--patch 'BLAST_KNOCK.speed=0'`).
 */
export const BLAST_KNOCK: KnockDef = { speed: 230, ticks: 7, decay: 0.8, slam: true };

export const POLLINATOR: WeaponDef = {
  id: 0,
  key: 'pollinator',
  name: 'Pistol',
  rarity: 0,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 4,
  magazine: 24,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.0),
  damage: 8,
  projectileSpeed: 680,
  spread: 0.08,
  rangePx: 240,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 12,
  feel: { shake: 0.12, kick: 1.5, recoil: 3, bloom: 3, color: 0xc0cbdc },
};

/**
 * A battle royale's Pistol (ADR-0189): everyone's from the start, so what a chest deals is worth finding — half the
 * magazine and a fifth slower. The Pistol all the same (its key, look, skins and sounds); elsewhere the Pollinator.
 */
export const ROYALE_PISTOL: WeaponDef = { ...POLLINATOR, id: 65, magazine: 12, fireIntervalTicks: 5 };

/**
 * A battle royale's grenades (ADR-0194): a round starts with none (ADR-0194 amended: they lie on health spots and spill
 * out of mini chests, `PICKUP`), at most `count` carried; the lobby island keeps `count` in hand (practice), never
 * dropped. The right button throws one at the crosshair — the cursor's reach, `min` … `max` px — from `hand` px
 * up, lobbed so it first comes down where you pointed, `gravity` px/s² pulling it, after `air` s (`min`'s first, `max`'s
 * second, between them by the reach: further is longer). It bounces off the ground (`bounce` of its fall back up,
 * `friction` of its speed kept), glances off walls and what stands (`wallBounce` kept; what stops only feet only below
 * `low` px), stops bouncing under `settle` px/s, rolls `roll` of its speed a tick, and bursts `fuseTicks` after it first
 * came down (ADR-0194 amended: how far you throw is when it goes): `radius` px, `damage` at its middle (half at the rim),
 * a blast's knock — its thrower's too (`GRENADE_THROW.selfBlast`). One every `cooldownTicks`.
 */
export const GRENADE = {
  count: 5,
  cooldownTicks: ticks(0.5),
  fuseTicks: ticks(0.5),
  hand: 9,
  air: [0.3, 0.8],
  gravity: 340,
  min: 28,
  max: 175,
  bounce: 0.38,
  friction: 0.45,
  wallBounce: 0.55,
  low: 7,
  settle: 30,
  roll: 0.8,
  radius: 46,
  damage: 75,
} as const;

/** The grenade as a source of damage (ADR-0194: the kill feed's, the stats'): thrown, never held. */
export const GRENADE_THROW: WeaponDef = {
  id: 66,
  key: 'handGrenade',
  name: 'Grenade',
  rarity: 4,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: GRENADE.radius,
  splashDamage: GRENADE.damage,
  fireIntervalTicks: GRENADE.cooldownTicks,
  magazine: GRENADE.count,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 0,
  spread: 0,
  rangePx: GRENADE.max,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 8,
  size: 4,
  selfBlast: true,
  feel: { shake: 0.3, kick: 2, recoil: 0, bloom: 0, color: 0x63c74d },
};

/**
 * What a battle royale's explosive slot holds (ADR-0201): grenades or C4, one kind at a time (`Gun.explosive`), as many
 * as `explosiveCount` of it (`Gun.grenades` counts whichever it is); or an Ejector Seat (ADR-0218), beacons (ADR-0220
 * amended), Wind-up gramophones (ADR-0221), or a fishing rod (ADR-0229: its count its baits). The wire has three bits for it
 * (`SelfState.explosives`): a ninth kind widens them.
 */
export const Explosive = { Grenade: 0, C4: 1, Ejector: 2, Beacon: 3, Gramophone: 4, Rod: 5 } as const;
export type Explosive = (typeof Explosive)[keyof typeof Explosive];

/**
 * A battle royale's C4 (ADR-0201): found as grenades are, carried in their slot, at most `count`. The right button throws
 * a charge as a grenade flies (`throwFrom`); it sticks to the first wall or thing it meets, or on a gardener of another
 * squad, else where it comes down. The right button again sets it off, no sooner than `armTicks` after the throw (a press
 * before waits for it): `radius` px, `damage` at its middle, half at its rim, a blast's knock. One charge out at a time;
 * one whose thrower is out goes dead (`gone`), never off.
 */
export const C4 = {
  count: 3,
  armTicks: ticks(0.35),
  radius: 56,
  damage: 95,
} as const;

/**
 * A battle royale's Ejector Seat (ADR-0218): rare, found as C4 is and carried in its slot, at most `count`. The right
 * button rockets its holder straight up out of it (`AIR.eject`), and they glide down under an umbrella. Its rocket bursts
 * under them as it goes (ADR-0218 amended): `radius` px, `damage` at its middle, half at its rim, a blast's knock, on
 * everyone but its rider and their squad (`EJECTOR_BLAST`).
 */
export const EJECTOR = { count: 1, radius: 46, damage: 60 } as const;

/**
 * A battle royale's beacon rockets (ADR-0220 amended: the crow became a rocket that lets out a drone): found as C4 is and
 * carried in its slot, at most `count`. The right button sends one at the nearest gardener of another squad on the map,
 * however far (none left: a little way ahead of its sender), never where the crosshair is. A charge kicks it out cold: up
 * off the hand at `kick.up` px/s, falling back at `kick.fall` px/s², drifting `kick.drift` px/s towards its prey, from
 * `kick.z` px up; `kickTicks` later its motor lights and it flies a curve up and over (`up` px off where it lit, `lead` of
 * the way on; `high` px over the spot; both lower for a prey nearer than `near` px) to `pop` px over its prey, homing on
 * them as it goes, at `speed` px/s gaining `accel` a second up to `top`, slower climbing and quicker diving (`climb` of
 * it). There it bursts in the air (hurting nobody) and lets out its drone: for `ticks` it follows its prey (the nearest
 * foe again once they are gone) at up to `chase` px/s, settling to `hover` px over the ground, and its squad sees round it
 * as a vision ward does (`radius` px all round, walls casting their shadows); then its battery dies and it drops, `sink`
 * px/s, for `leaveTicks`, and is gone. Every squad sees it in the sky, and nothing can hit it: it lasts its time.
 */
export const BEACON = {
  count: 2,
  kick: { up: 150, fall: 430, drift: 36, z: 7 },
  kickTicks: ticks(0.36),
  up: 96,
  lead: 0.18,
  high: 88,
  near: 220,
  pop: 50,
  speed: 330,
  accel: 1500,
  top: 720,
  climb: 0.35,
  ticks: ticks(5),
  chase: 130,
  hover: 34,
  sink: 45,
  leaveTicks: ticks(0.7),
  radius: 120,
} as const;

/** A beacon's legs (ADR-0220 amended, `Beacon.phase`): kicked out cold, flying lit, its drone out, its battery dying. */
export const BeaconPhase = { Kick: 0, Flight: 1, Drone: 2, Out: 3 } as const;

/**
 * A battle royale's Wind-up gramophone (ADR-0221): found as C4 is and carried in its slot, at most `count`. The right
 * button throws one as a grenade flies (`throwFrom`, `stepGrenade`: it bounces and rolls); `windTicks` after it first
 * comes down its record plays for `playTicks`: the gunfire of the gun its thrower held as they threw it, in bursts — one
 * every `every` ticks, as late as `lag` into it, of `burst` shots at that gun's own rate (`minInterval` ticks apart at
 * the quickest), each `spread` either side of a heading the burst draws. Nothing is fired: each shot is a `gramophone`
 * event that sounds and is cued round whoever hears it out of sight as another squad's shot is (ADR-0191), and the bots
 * hear it so (ADR-0209). Seen, it is a gramophone. Playing, it is a target (ADR-0221 amended): `hurt` px round, `hp`
 * health, and another squad's blow that takes the last silences it for good.
 */
export const GRAMOPHONE = {
  count: 2,
  windTicks: ticks(0.6),
  playTicks: ticks(9),
  every: ticks(1.4),
  lag: ticks(0.5),
  burst: [2, 6],
  minInterval: 3,
  spread: 0.12,
  hp: 24,
  hurt: 6,
} as const;

/**
 * A battle royale's fishing rod (ADR-0229, content `FISHING`): it lies on its map's spots (`MapDef.rods`), carried in the
 * explosive slot, `baits` of them: a catch, a fish lost or a line cut spends one.
 */
export const ROD = { baits: 3 } as const;

/** How many of explosive `kind` a gardener may carry (ADR-0201). */
export const explosiveCount = (kind: number): number =>
  kind === Explosive.C4
    ? C4.count
    : kind === Explosive.Ejector
      ? EJECTOR.count
      : kind === Explosive.Beacon
        ? BEACON.count
        : kind === Explosive.Gramophone
          ? GRAMOPHONE.count
          : kind === Explosive.Rod
            ? ROD.baits
            : GRENADE.count;

/** C4 as a source of damage (ADR-0201: the kill feed's, the stats'): thrown and set off, never held. */
export const C4_CHARGE: WeaponDef = {
  ...GRENADE_THROW,
  id: 67,
  key: 'c4',
  name: 'C4',
  selfBlast: false,
  splashRadius: C4.radius,
  splashDamage: C4.damage,
  magazine: C4.count,
  feel: { shake: 0.4, kick: 2, recoil: 0, bloom: 0, color: 0xe43b44 },
};

/** An Ejector Seat's rocket as a source of damage (ADR-0218 amended: the kill feed's, the stats'): never its rider's. */
export const EJECTOR_BLAST: WeaponDef = {
  ...GRENADE_THROW,
  id: 71,
  key: 'ejector',
  name: 'Ejector Seat',
  selfBlast: false,
  splashRadius: EJECTOR.radius,
  splashDamage: EJECTOR.damage,
  magazine: EJECTOR.count,
  rangePx: 0,
  feel: { shake: 0.35, kick: 2, recoil: 0, bloom: 0, color: 0xfee761 },
};

export const THORNFIRE: WeaponDef = {
  id: 1,
  key: 'thornfire',
  name: 'Rifle',
  rarity: 1,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 3,
  magazine: 30,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.4),
  damage: 10,
  projectileSpeed: 820,
  spread: 0.05,
  rangePx: 320,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 14,
  feel: { shake: 0.1, kick: 1.4, recoil: 2.5, bloom: 2, color: 0x63c74d },
};

export const LONGSTEM: WeaponDef = {
  id: 2,
  key: 'longstem',
  name: 'Sniper',
  rarity: 2,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 27,
  magazine: 5,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.9),
  damage: 55,
  projectileSpeed: 1300,
  spread: 0.004,
  rangePx: 560,
  pierce: 3,
  royalePierce: 0,
  scope: true,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.8,
  muzzleDistance: 17,
  feel: { shake: 0.42, kick: 5, recoil: 6, bloom: 9, color: 0x0099db },
};

/**
 * A battle royale's Sniper (the owner, 2026-10-26): one round, then a short reload (1.2 s, a shot every 1.5 s) — every
 * shot counts, never a burst. The Sniper all the same (its key, look, scope and sounds).
 */
export const ROYALE_SNIPER: WeaponDef = {
  ...LONGSTEM,
  id: 70,
  fireIntervalTicks: 9,
  magazine: 1,
  reloadTicks: ticks(1.2),
};

export const WHACKER: WeaponDef = {
  id: 3,
  key: 'whacker',
  name: 'Minigun',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 150,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(3),
  // 7 until ADR-0040: the fastest kill of any gun (0.83 s from the first hit in the duel lab), now 0.9 s.
  damage: 6,
  projectileSpeed: 700,
  spread: 0.2,
  rangePx: 280,
  pierce: 0,
  spinUpTicks: ticks(0.4),
  firingMoveMultiplier: 0.55,
  muzzleDistance: 15,
  feel: { shake: 0.06, kick: 0.7, recoil: 1.5, bloom: 1.2, color: 0xb55088 },
};

/**
 * A battle royale's Minigun (ADR-0189 amended): a burst, never a hose. Its barrel takes 0.6 s to spin up (0.4 s
 * elsewhere: a foe met round a corner gets the first shots), its drum holds 100 (150: three seconds of fire) and takes 4 s
 * to fill (3). Duel lab against the bots' Rifle (`royale-duel --foe thornfire`): 37% of the fights won, from 59% (the Rifle
 * itself 66%, the Shotgun 47%) — a gun for close range and ambushes, spun up before the peek. The Minigun all the same
 * (its key, look and sounds).
 */
export const ROYALE_MINIGUN: WeaponDef = {
  ...WHACKER,
  id: 68,
  spinUpTicks: ticks(0.6),
  magazine: 100,
  reloadTicks: ticks(4),
};

/**
 * Flamethrower (a garden weed burner, ADR-0019; took the Seedspreader's slot): a short cone of flames that pass
 * through two targets each — melts minion waves and anyone who comes close, useless at range. A flame burns what its
 * puff touches (`size`, ADR-0040: it was a point inside a puff drawn up to 12 px across), and firing it doesn't slow
 * you — it has to catch people.
 */
export const WEED_BURNER: WeaponDef = {
  id: 4,
  key: 'burner',
  name: 'Flamethrower',
  rarity: 2,
  turret: false,
  pellets: 2,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 2,
  magazine: 60,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.8),
  damage: 5,
  projectileSpeed: 300,
  spread: 0.34,
  rangePx: 135,
  pierce: 2,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 14,
  size: 8,
  flame: true,
  // ADR-0083: keeps burning a little after the flames are gone.
  burn: { ticks: ticks(2), damage: 3, every: 10 },
  feel: { shake: 0.03, kick: 0.3, recoil: 1, bloom: 0.6, color: 0xfeae34 },
};

export const ROOT_ROCKET: WeaponDef = {
  id: 5,
  key: 'rocket',
  name: 'Bazooka',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 50,
  splashDamage: 55,
  fireIntervalTicks: 24,
  magazine: 2,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.5),
  damage: 70,
  projectileSpeed: 540,
  spread: 0.02,
  rangePx: 360,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.85,
  muzzleDistance: 16,
  seek: true,
  selfBlast: true,
  feel: { shake: 0.38, kick: 4.5, recoil: 6, bloom: 10, color: 0xf77622 },
};

/**
 * Classic's lane cannons' gun (ADR-0016): nothing fires it since Classic is gone (ADR-0209); kept for its id, a weapon's
 * index in `WEAPONS`.
 */
export const CANNON_SHOT: WeaponDef = {
  id: 6,
  key: 'cannon',
  name: 'Cannon',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: POLLINATOR.fireIntervalTicks,
  magazine: 12,
  reserve: 0,
  reloadTicks: POLLINATOR.reloadTicks,
  damage: POLLINATOR.damage,
  projectileSpeed: POLLINATOR.projectileSpeed,
  spread: POLLINATOR.spread,
  rangePx: 170,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 10,
  feel: { shake: 0.1, kick: 1, recoil: 2, bloom: 0, color: 0x5a6988 },
};

/** The Snapdragon's thorny seeds: slow enough to dodge, and they reach as far as its leash (SNAPDRAGON). */
export const SEED_SPIT: WeaponDef = {
  id: 7,
  key: 'seed',
  name: 'Thorn Seed',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 17,
  projectileSpeed: 170,
  spread: 0,
  rangePx: 360,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 14,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xb55088 },
};

/**
 * Classic's base towers' homing missile: nothing fires it since Classic is gone (ADR-0209); kept for its id, a weapon's
 * index in `WEAPONS`.
 */
export const TOWER_MISSILE: WeaponDef = {
  id: 8,
  key: 'missile',
  name: 'Tower Missile',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(1.3),
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 30,
  projectileSpeed: 190,
  spread: 0,
  /** Flight budget (it homes, so this only ends a chase that goes on and on). */
  rangePx: 600,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  homing: true,
  feel: { shake: 0.1, kick: 1, recoil: 3, bloom: 0, color: 0xe43b44 },
};

/**
 * Classic's Thorn Spit blessing's thorn (ADR-0014): nothing spits it since Classic is gone (ADR-0209); kept for its id, a
 * weapon's index in `WEAPONS`.
 */
export const THORN: WeaponDef = {
  id: 9,
  key: 'thorn',
  name: 'Thorn',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 9,
  projectileSpeed: 210,
  spread: 0,
  rangePx: 130,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 5,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0x63c74d },
};

/** A continuous laser (ADR-0019): a steady beam that hits the instant it touches, for as long as you hold. */
export const SUNBEAM: WeaponDef = {
  id: 10,
  key: 'laser',
  name: 'Laser',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 90,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.8),
  // 4 until ADR-0040: it can't be dodged and aim help snaps it on, so once it touched you, you were dead (1.07 s from
  // the first hit, 76% of duels won); now 1.7 s, 55%.
  damage: 3,
  // Crosses its whole range within the tick after it is fired.
  projectileSpeed: 300 * 30,
  spread: 0,
  rangePx: 300,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.75,
  muzzleDistance: 12,
  beam: true,
  // Sun-yellow (the icy cyan is the Frostbite's).
  feel: { shake: 0.015, kick: 0.15, recoil: 0.5, bloom: 0.2, color: 0xfee761 },
};

/**
 * A strike called on a spot (ADR-0019, ADR-0099): it lands `delayTicks` after the spot is picked (a warning marks it for
 * everyone), then hurts every hostile thing within `radius` px — `damage` at the centre down to `edgeDamage` at the rim
 * (measured to the target's edge). A base anywhere in it loses `baseShare` of its full health instead. Whatever lives
 * through it is thrown out by `knock` (half of it at the rim). While picking, the view zooms out to `zoom`. A `sweep`
 * (the alien mothership's laser) instead burns along a path the caller draws, at most `length` px, over `ticks`: every
 * `every` ticks everything within `radius` of the beam takes `damage` (a base `baseShare` of its full health).
 */
export interface StrikeDef {
  readonly delayTicks: number;
  readonly radius: number;
  readonly damage: number;
  readonly edgeDamage: number;
  readonly baseShare: number;
  readonly knock: KnockDef;
  readonly zoom: number;
  readonly sweep?: {
    readonly ticks: number;
    readonly every: number;
    readonly length: number;
    readonly min: number;
    /** A bombing run (the Air Strike, ADR-0159): every pulse is a rocket's blast, not the laser's burn. */
    readonly bombs?: boolean;
  };
}

/**
 * The atom bomb's strike (ADR-0019): it lands `delayTicks` after the spot is picked (a warning marks it for
 * everyone), then hurts every hostile thing within `radius` px — `damage` at the centre down to `edgeDamage` at the
 * rim (measured to the target's edge). A base anywhere in the blast loses `baseShare` of its full health instead: the
 * bomb is a siege weapon. Whatever lives through it is thrown out of the blast by `knock` (half of it at the rim,
 * ADR-0036). While picking, the view zooms out to `zoom`.
 */
export const NUKE: StrikeDef = {
  delayTicks: ticks(3),
  radius: 110,
  damage: 600,
  edgeDamage: 150,
  baseShare: 0.33,
  knock: { speed: 320, ticks: 9, decay: 0.82, slam: true },
  zoom: 0.5,
};

/**
 * The alien mothership's laser (ADR-0099): you draw its path on the map; a huge saucer comes over its start and burns a
 * laser from the sky down along it.
 */
export const UFO: StrikeDef = {
  delayTicks: ticks(2),
  radius: 18,
  damage: 28,
  edgeDamage: 28,
  baseShare: 0.012,
  knock: { speed: 0, ticks: 1, decay: 1, slam: false },
  zoom: 0.5,
  sweep: { ticks: ticks(3), every: 3, length: 360, min: 40 },
};

/**
 * The Air Strike's bombing run (ADR-0159): you draw its path like the mothership's; a jet comes in low along it and ripples
 * rockets down it, each a blast thrown wide.
 */
export const JETS: StrikeDef = {
  delayTicks: ticks(2),
  radius: 32,
  damage: 60,
  edgeDamage: 30,
  baseShare: 0.02,
  knock: { speed: 220, ticks: 7, decay: 0.8, slam: true },
  zoom: 0.5,
  sweep: { ticks: ticks(1.5), every: 6, length: 380, min: 40, bombs: true },
};

/** The atom bomb (ADR-0019): one use; click to pick a spot on the map, it lands there NUKE.delayTicks later. */
export const ATOM_BOMB: WeaponDef = {
  id: 11,
  key: 'nuke',
  name: 'Atom Bomb',
  rarity: 5,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 1,
  spread: 0,
  rangePx: 0,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 8,
  strike: NUKE,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0x63c74d },
};

/** The Spore Shroom's spore (a jungle creep, ADR-0019): slow and dodgeable, like the Snapdragon's seeds. */
export const SPORE: WeaponDef = {
  id: 12,
  key: 'spore',
  name: 'Spore',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 10,
  projectileSpeed: 170,
  spread: 0,
  rangePx: 160,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xb55088 },
};

/**
 * A curled Hedgehog's spine (ADR-0053): a bullet that meets the ball comes straight back as one of these. Short, and
 * slow enough to step aside from (270 px/s since ADR-0080, was 360), so a shooter who keeps their distance or moves is
 * safe; it spares the jungle's own.
 */
export const SPINE: WeaponDef = {
  id: 18,
  key: 'spine',
  name: 'Spine',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 5,
  projectileSpeed: 270,
  spread: 0,
  rangePx: 200,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 8,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xe4a672 },
};

/** A Frostbite hit slows (ADR-0031): a frosted thing covers this much of its way (a knock is not slowed). */
export const FROST = { speed: 0.45 } as const;

/**
 * The freeze rifle: rifle-like, weaker than the Thornfire, but every hit frosts the target (players, minions, creeps,
 * bosses) for `freezeTicks` — it moves at `FROST.speed`; hits while frosted top it back up to 2 s.
 */
export const FROSTBITE: WeaponDef = {
  id: 13,
  key: 'freeze',
  name: 'Freeze Gun',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 4,
  magazine: 24,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.6),
  damage: 7,
  projectileSpeed: 760,
  spread: 0.05,
  rangePx: 300,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 14,
  freezeTicks: ticks(2),
  feel: { shake: 0.08, kick: 1.2, recoil: 2.5, bloom: 2, color: 0x2ce8f5 },
};

/**
 * The grenade launcher: lobs pinecone grenades forward that burst the moment an enemy is close (`fuse`), on a wall or
 * at the end of their flight — like a rocket's blast, without the rocket's seeking.
 */
export const CONE_LOBBER: WeaponDef = {
  id: 14,
  key: 'grenade',
  name: 'Grenade Launcher',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 44,
  splashDamage: 65,
  fireIntervalTicks: ticks(0.7),
  magazine: 4,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.8),
  // Bursts before it touches anyone: all its damage is the blast.
  damage: 0,
  projectileSpeed: 330,
  spread: 0.03,
  rangePx: 300,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.85,
  muzzleDistance: 13,
  fuse: 16,
  feel: { shake: 0.3, kick: 3.5, recoil: 5, bloom: 7, color: 0xb86f50 },
};

/**
 * The Railgun (ADR-0035): hold to charge (1.3 s, the barrel glowing, you slowed), let go for an instant rail that
 * crosses its range within the tick. Damage and piercing grow with the charge; a full charge goes through everything
 * in its line, burns a tunnel through the first hedge or thicket it meets (ADR-0038), knocks what it hits back and kicks
 * you back too. Let go too early (under `min`) and nothing fires.
 */
export const CORN_RAIL: WeaponDef = {
  id: 15,
  key: 'rail',
  name: 'Railgun',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.3),
  magazine: 4,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.8),
  // At full charge; less for a shorter one.
  damage: 90,
  // Crosses its whole range within the tick after it is fired.
  projectileSpeed: 420 * 30,
  spread: 0,
  rangePx: 420,
  // Set per shot from the charge.
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.55,
  muzzleDistance: 16,
  charge: { ticks: ticks(1.3), min: ticks(0.25), pierce: 4 },
  // Half a tile: a gardener (body radius 5) walks along the rail's line with room to spare.
  tunnel: { radius: 8 },
  shove: 170,
  knock: { speed: 170, ticks: 6, decay: 0.8, slam: true },
  feel: { shake: 0.55, kick: 7, recoil: 8, bloom: 12, color: 0xf6757a },
};

/**
 * The Katana (ADR-0035): click to dash along your aim, cutting everything on the way — no ammo, no reload, only the
 * delay between dashes. Holding it you wear a 100 HP guard, so you can dash into a fight and live.
 */
export const PRUNER: WeaponDef = {
  id: 16,
  key: 'katana',
  name: 'Katana',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.7),
  magazine: 3,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.4),
  // A first-wave minion in one cut.
  damage: 40,
  endless: true,
  projectileSpeed: 390,
  spread: 0,
  // The dash's length (ticks × speed).
  rangePx: 78,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 10,
  dash: { ticks: 6, speed: 390, reach: 9 },
  guard: 100,
  // Cut things are thrown aside, off the dash's path.
  knock: { speed: 120, ticks: 5, decay: 0.8, slam: false },
  feel: { shake: 0.2, kick: 2.5, recoil: 4, bloom: 4, color: 0xe43b44 },
};

/**
 * The Robo Fist (ADR-0035): a steel gauntlet that fires a claw on a vine. Whatever the claw catches is yanked to you —
 * and the moment it lands, the fist punches it away along your aim: into a hedge, it's a SPLAT.
 */
export const VINE_FIST: WeaponDef = {
  id: 17,
  key: 'fist',
  name: 'Robo Fist',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.8),
  magazine: 3,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.8),
  // The claw's bite; the punch is `hook.punchDamage`.
  damage: 8,
  projectileSpeed: 560,
  spread: 0,
  rangePx: 210,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.85,
  muzzleDistance: 14,
  size: 2,
  hook: {
    pullSpeed: 520,
    gap: 3,
    punchDamage: 42,
    punch: { speed: 420, ticks: 10, decay: 0.86, slam: true },
    // From the punch: the throw's third of a second and most of a second dazed where it lands.
    stun: ticks(1.2),
  },
  feel: { shake: 0.25, kick: 3, recoil: 5, bloom: 5, color: 0xd77643 },
};

/**
 * The Homing Gun (ADR-0078): a jar of fireflies. Each one bends hard onto what it can see — round a hedge's corner onto
 * someone hiding behind it — so it almost never misses, but a firefly bites for little: it wins by landing every shot
 * from cover, not by trading. Minions and creeps in its way soak it; a Buzz Saw's blade parries it only from the front.
 */
export const FIREFLY: WeaponDef = {
  id: 19,
  key: 'firefly',
  name: 'Homing Gun',
  rarity: 2,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 3,
  magazine: 24,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.5),
  // Duel lab (ADR-0078): 6 a firefly at 7.5 a second killed in 4.4 s (25% against the field), 6 at 10 in 2.5 s (37%).
  damage: 7,
  // Slow enough that the curve reads.
  projectileSpeed: 400,
  spread: 0.14,
  // A flight budget: a curved flight is longer than its reach.
  rangePx: 340,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 13,
  bend: { turnPerPx: 0.03, range: 190, cone: 1.75 },
  feel: { shake: 0.08, kick: 1, recoil: 2, bloom: 2, color: 0xfee761 },
};

/**
 * A battle royale's Homing Gun (ADR-0189 amended): its fireflies make up a near miss, no more. They bend onto someone
 * close to where they fly (`bend`: within 0.5 rad either side and 150 px, at under a quarter of the turn: about 80 px
 * made up over 150 px, from enough to turn round), so they no longer curl round a hedge onto whoever is behind it or swing
 * onto someone beside you; landed, a firefly bites for 6 (60 a second to the Rifle's 100: a gardener 5 a hit; the owner's
 * nerf of 2026-10-31 to 5 took it to 4 and was eased back on 2026-11-04). Duel lab against the bots' Rifle (`royale-duel
 * --foe thornfire --duels 200`): 20% of the fights won (5: 8%, 7: 39%; the Rifle in your hands 55%). The Homing Gun all
 * the same (its key, look and sounds).
 */
export const ROYALE_HOMING: WeaponDef = {
  ...FIREFLY,
  id: 69,
  damage: 6,
  bend: { turnPerPx: 0.007, range: 150, cone: 0.5 },
};

/**
 * The Hive Launcher (ADR-0078): lobs a beehive that bursts — near an enemy (`fuse`), on a wall or at the end of its flight
 * — into five angry bees (BEE). They hunt whatever is nearest (a gardener first), fly over hedges and sting once each.
 * The hive itself hurts nobody: all its damage is the bees', and bees can be shot down.
 */
export const BEEHIVE: WeaponDef = {
  id: 20,
  key: 'hive',
  name: 'Hive Launcher',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.75),
  magazine: 3,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2.2),
  damage: 0,
  projectileSpeed: 290,
  spread: 0.03,
  rangePx: 230,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 15,
  fuse: 12,
  swarm: { bees: 5 },
  feel: { shake: 0.22, kick: 3, recoil: 4, bloom: 6, color: 0xfeae34 },
};

/**
 * A Hive Launcher's bees (ADR-0078): `hp` (one bullet kills one), no body — they fly over hedges — and `lifeTicks` to
 * live. Out of the hive at `burstSpeed`, each hunts the hostile gardener, minion, creep, boss or dummy nearest it within
 * `sense` px (a gardener counts as `playerBias` × as near), keeping to its quarry while it stays within `sense` ×
 * `keep`; it flies at `speed` px/s, turning at most `turn` rad/s (a strafing target makes it overshoot), wobbling
 * `wobble` rad. Within `reach` px of its quarry's edge it stings for `sting` — and dies, like a honeybee. With nothing
 * to hunt it circles the burst `orbit` px out, at `idle` × its speed.
 */
export const BEE = {
  hp: 4,
  hurtRadius: 3,
  lifeTicks: ticks(5),
  burstSpeed: 110,
  speed: 150,
  idle: 0.6,
  turn: 8,
  wobble: 0.5,
  sense: 170,
  keep: 1.5,
  playerBias: 0.6,
  reach: 2,
  sting: 10,
  orbit: 22,
} as const;

/**
 * The Buzz Saw (ADR-0078): hold to rev it (`spinUpTicks`) — you run faster revving — and the blade grinds whatever it
 * touches, 15 bites a second. Its front parries gunfire (not turrets', not blasts, not bees) and, being endless,
 * never runs out of fuel (`saw.parryCost` only counts for a saw that isn't). The melee answer to the Katana's dash and guard.
 */
export const BUZZSAW: WeaponDef = {
  id: 21,
  key: 'saw',
  name: 'Buzz Saw',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 2,
  // Endless like the other melee guns: the blade never runs dry, and parrying costs no fuel.
  magazine: 150,
  reserve: INFINITE_RESERVE,
  reloadTicks: 1,
  endless: true,
  damage: 7,
  projectileSpeed: 1,
  spread: 0,
  // The blade's reach from its holder's centre (the muzzle + the blade), for bots.
  rangePx: 27,
  pierce: 0,
  spinUpTicks: ticks(0.2),
  firingMoveMultiplier: 1.25,
  muzzleDistance: 13,
  // Duel lab (ADR-0078): at half a fuel a point, parries barely mattered (61–64% against the field); a point a point lets
  // a rifle or a laser burn it dry in about a second and a half.
  saw: { radius: 8, parry: 1.05, parryCost: 1 },
  feel: { shake: 0.05, kick: 0.4, recoil: 1, bloom: 0.5, color: 0xbe4a2f },
};

/**
 * Shurikens: a fan of three throwing stars, each biting for little. Up close all three land (120 a second); the fan opens
 * wide, so at range few find them — a spray that wins by closing in.
 */
export const SHURIKEN: WeaponDef = {
  id: 22,
  key: 'shuriken',
  name: 'Shurikens',
  rarity: 2,
  turret: false,
  pellets: 3,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 6,
  magazine: 20,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.4),
  // Duel lab (ADR-0082): 4 a star in a 0.3 rad fan won 25% against the field (it lost even up close); 6 in 0.24, 49%.
  // ADR-0083: a wider fan (0.42) thrown less often (every 6 ticks), 8 a star.
  damage: 8,
  projectileSpeed: 640,
  spread: 0.42,
  rangePx: 230,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 10,
  feel: { shake: 0.05, kick: 0.8, recoil: 1.5, bloom: 2, color: 0x8b9bb4 },
};

/**
 * The Toxic Launcher: lobs a canister of sludge that bursts as soon as an enemy is close (`fuse`), on a wall or at the end
 * of its flight, into a puddle (`puddle`): a splash on what is in it, then a bite every quarter second on whatever stands
 * in it for five seconds. Middling in everything — rate, magazine, reload, damage — it wins by denying ground: a
 * minion wave marching through, a doorway, the spot a foe wants to stand on.
 */
export const SLUDGE: WeaponDef = {
  id: 23,
  key: 'toxic',
  name: 'Toxic Launcher',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.6),
  magazine: 4,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2),
  // Bursts before it touches anyone: all its damage is the puddle's.
  damage: 0,
  projectileSpeed: 340,
  spread: 0.03,
  rangePx: 260,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 14,
  fuse: 10,
  // Duel lab (ADR-0082): a 28 px puddle splashing 18 and biting 7 won 28% against the field; this one 47%.
  puddle: { radius: 32, ticks: ticks(5), impact: 28, damage: 8, every: 8 },
  feel: { shake: 0.2, kick: 2.5, recoil: 4, bloom: 5, color: 0x9be05a },
};

/**
 * The Shotgun: five pellets at once in a wide fan, six shells (three until ADR-0099), slow to pump and slow to reload. Point-blank all five
 * land (85); past a few gardeners' lengths the fan is wider than a gardener.
 */
export const SHOTGUN: WeaponDef = {
  id: 24,
  key: 'shotgun',
  name: 'Shotgun',
  rarity: 2,
  turret: false,
  pellets: 5,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.7),
  magazine: 6,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2.2),
  // Duel lab (ADR-0083): 14 a pellet in 0.62 rad, 0.75 s, 2.4 s reload won 31% against the field; this, 51%.
  damage: 17,
  projectileSpeed: 720,
  spread: 0.55,
  rangePx: 200,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 16,
  feel: { shake: 0.4, kick: 4.5, recoil: 6, bloom: 10, color: 0xe4a672 },
};

/**
 * The Twister: throws a whirlwind that drifts slowly ahead, through everything in its way, and flings what it catches
 * out of its path — into a hedge, SPLAT (the knock slams). Stopped by walls.
 */
export const TORNADO: WeaponDef = {
  id: 25,
  key: 'tornado',
  name: 'Twister',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.6),
  magazine: 4,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2),
  // Duel lab (ADR-0083): 18 a hit at 150 px/s, 3 every 0.9 s, won 9% against the field (too slow to hit); this, 51%.
  damage: 42,
  projectileSpeed: 260,
  spread: 0,
  rangePx: 300,
  pierce: 255,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 14,
  size: 14,
  whirl: true,
  knock: { speed: 260, ticks: 8, decay: 0.85, slam: true },
  feel: { shake: 0.2, kick: 2, recoil: 3, bloom: 5, color: 0x9be8ff },
};

/**
 * Alien Abduction: a ray pistol whose bolt calls down a flying saucer on what it hits — half its health gone, held in the
 * beam, dropped somewhere else. Missing costs almost nothing (a quick recharge); a catch leaves it recharging for 3 s.
 * It never kills: in the duel lab it wins 1% (a duel ends at the first kill); it is a gun for a team fight.
 */
export const ABDUCTOR: WeaponDef = {
  id: 26,
  key: 'abduct',
  name: 'Alien Abduction',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.3),
  magazine: 1,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(0.6),
  damage: 0,
  projectileSpeed: 620,
  spread: 0.02,
  rangePx: 300,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 13,
  abduct: { hold: ticks(2), share: 0.5, reload: ticks(3), near: 160, far: 320 },
  feel: { shake: 0.1, kick: 1.5, recoil: 3, bloom: 4, color: 0x2ce8f5 },
};

/**
 * The Berserker Sword: a huge blade swung in a full circle — every swing cuts everything round its holder at once (the
 * Buzz Saw's bite, centred on the holder, no parrying). Swinging it you rush (1.3× your pace) and wear a 60 HP guard.
 */
export const BERSERKER: WeaponDef = {
  id: 27,
  key: 'sword',
  name: 'Berserker Sword',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.4),
  magazine: 10,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.2),
  // Duel lab (ADR-0083): 30 a swing every 0.6 s in 28 px, no guard, won 12% against the field (shot down on the way in);
  // this — and a berserker's rush while swinging, and a 60 HP guard — 48%.
  damage: 40,
  endless: true,
  projectileSpeed: 1,
  spread: 0,
  // The blade's reach round its holder, for bots.
  rangePx: 44,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1.3,
  muzzleDistance: 0,
  saw: { radius: 38, parry: 0, parryCost: 0 },
  guard: 60,
  feel: { shake: 0.25, kick: 0, recoil: 0, bloom: 4, color: 0xa22633 },
};

/**
 * Poison Darts: quick darts that sting a little and poison — every dart one more stack, each hit keeping the poison 5 s
 * longer: the boss killer, the longer you keep on it the harder it bites. Up to 12 stacks: ~29 a second of darts and
 * 12 × 12 of poison is ~173 DPS on one target, 1.5× the hardest-hitting gun's (the Flamethrower's ~117 on a boss, its
 * reloads counted; uncapped, 20 s of darts on a boss averaged 310 and kept climbing).
 */
export const POISON_DART: WeaponDef = {
  id: 28,
  key: 'dart',
  name: 'Poison Darts',
  rarity: 2,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 8,
  magazine: 8,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.5),
  // Duel lab (ADR-0083): 8 a dart and a 3-a-beat poison won 29% against the field; this, 59%.
  damage: 13,
  projectileSpeed: 900,
  spread: 0.02,
  rangePx: 330,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 13,
  burn: { ticks: ticks(5), damage: 4, every: 10, stack: 12 },
  feel: { shake: 0.06, kick: 0.8, recoil: 2, bloom: 2, color: 0xb55088 },
};

export const CUPID_BOW: WeaponDef = {
  id: 29,
  key: 'cupid',
  name: "Cupid's Bow",
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.25),
  magazine: 5,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.6),
  damage: 80,
  projectileSpeed: 950,
  spread: 0,
  rangePx: 420,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.6,
  muzzleDistance: 12,
  charge: { ticks: ticks(1), min: ticks(0.2), pierce: 0 },
  charm: true,
  feel: { shake: 0.25, kick: 3, recoil: 4, bloom: 6, color: 0xf6757a },
};

export const DUBSTEP: WeaponDef = {
  id: 30,
  key: 'dubstep',
  name: 'Dubstep Gun',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 5,
  magazine: 36,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2),
  damage: 12,
  projectileSpeed: 420,
  spread: 0,
  rangePx: 230,
  pierce: 3,
  spinUpTicks: ticks(0.35),
  firingMoveMultiplier: 0.85,
  muzzleDistance: 14,
  size: 7,
  wave: true,
  bounce: true,
  feel: { shake: 0.07, kick: 1.2, recoil: 2.5, bloom: 3, color: 0xb55088 },
};

export const FIREWORK: WeaponDef = {
  id: 31,
  key: 'firework',
  name: 'Firework Launcher',
  rarity: 3,
  turret: false,
  pellets: 5,
  splashRadius: 26,
  splashDamage: 24,
  fireIntervalTicks: ticks(0.8),
  magazine: 3,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2.2),
  damage: 0,
  projectileSpeed: 360,
  spread: 0.6,
  rangePx: 280,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 14,
  fuse: 8,
  stagger: 0.45,
  feel: { shake: 0.25, kick: 3, recoil: 5, bloom: 7, color: 0xe43b44 },
};

export const MECHA: WeaponDef = {
  id: 32,
  key: 'mecha',
  name: 'Mecha Suit',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 2,
  magazine: 80,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2.5),
  // 7 until ADR-0108.
  damage: 8,
  projectileSpeed: 720,
  spread: 0.14,
  rangePx: 290,
  pierce: 0,
  spinUpTicks: ticks(0.3),
  firingMoveMultiplier: 1,
  muzzleDistance: 22,
  dual: 9,
  // 80 and 0.35 until ADR-0108 (a boss's reward now, a notch stronger).
  guard: 110,
  walk: 0.4,
  hurt: 12,
  transform: true,
  // Three quarters of a crate gun's 30 s.
  time: ticks(22.5),
  alt: { weapon: 34, shots: 2, cooldown: ticks(7) },
  feel: { shake: 0.07, kick: 0.9, recoil: 1.8, bloom: 1.3, color: 0x8b9bb4 },
};

export const GRIMOIRE: WeaponDef = {
  id: 33,
  key: 'demon',
  name: 'Demon Grimoire',
  rarity: 5,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 1,
  spread: 0,
  // Bots read it from here: they open the book when something hostile is this near.
  rangePx: 220,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 0,
  summon: { ticks: ticks(1.6), radius: 24, blood: 25 },
  feel: { shake: 0.3, kick: 0, recoil: 0, bloom: 0, color: 0xa22633 },
};

export const WAND: WeaponDef = {
  id: 35,
  key: 'wand',
  name: 'Magic Wand',
  rarity: 3,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 3,
  magazine: 45,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.6),
  damage: 8,
  // Bolts fall at once: bots lead nothing.
  projectileSpeed: 6000,
  spread: 0,
  rangePx: 260,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.85,
  muzzleDistance: 12,
  storm: { follow: 110, radius: 26, scatter: 14 },
  feel: { shake: 0.06, kick: 0, recoil: 1.5, bloom: 0, color: 0x9b7bff },
};

/** Alien Attack (ADR-0099): one use; draw a path on the map, a mothership burns a laser along it (UFO). */
export const ALIEN_ATTACK: WeaponDef = {
  id: 37,
  key: 'alien',
  name: 'Alien Attack',
  rarity: 5,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 1,
  spread: 0,
  rangePx: 0,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 8,
  strike: UFO,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0x2ce8f5 },
};

export const MECH_ROCKET: WeaponDef = {
  id: 34,
  key: 'mechRocket',
  name: 'Mecha Rocket',
  rarity: 4,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: 30,
  splashDamage: 28,
  fireIntervalTicks: 1,
  magazine: 2,
  reserve: 0,
  reloadTicks: 1,
  damage: 30,
  projectileSpeed: 380,
  spread: 0,
  rangePx: 320,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  seek: true,
  feel: { shake: 0.3, kick: 3, recoil: 0, bloom: 4, color: 0xf77622 },
};

/**
 * A Spitter's acid (Zombie Nights, ADR-0098): a slow glob, dodgeable like the Snapdragon's seeds; it hits for the
 * spitter's own damage (scaled by the night) and harder against builds (the zombie's `vsBuild`).
 */
export const ZOMBIE_ACID: WeaponDef = {
  id: 36,
  key: 'acid',
  name: 'Acid',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 12,
  projectileSpeed: 180,
  spread: 0.05,
  // Past `SPITTER.range` (a turret's reach and more).
  rangePx: 260,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0x2ce8f5 },
};

/**
 * A Moon Altar's bolt (Zombie Nights, ADR-0098 amended): a slow mote of moonlight, one of a ring the altar fires out all
 * round it; it hits for the altar's own damage (scaled by the night) and its `vsBuild` share on builds.
 */
export const ALTAR_BOLT: WeaponDef = {
  id: 46,
  key: 'bolt',
  name: 'Moon Bolt',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 12,
  projectileSpeed: 110,
  spread: 0,
  rangePx: 240,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 8,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xf6757a },
};

/**
 * A Lobber's rotten pumpkin (Zombie Nights, ADR-0098 amended): lobbed over everything onto where its mark stood, it bursts
 * there for the Lobber's own damage (scaled by the night) in `splashRadius`, harder against builds (its `vsBuild`).
 */
export const ROTTEN_GOURD: WeaponDef = {
  id: 47,
  key: 'gourd',
  name: 'Rotten Pumpkin',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 24,
  splashDamage: 16,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 150,
  spread: 0,
  rangePx: 220,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  lob: { min: 30 },
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xd77643 },
};

/** The RC tractor's gun (ADR-0101): a pea shooter on its bonnet, firing where the tractor points. */
export const TRACTOR_GUN: WeaponDef = {
  id: 39,
  key: 'tractorGun',
  name: 'RC Tractor',
  rarity: 4,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 7,
  projectileSpeed: 620,
  spread: 0.06,
  rangePx: 200,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xf77622 },
};

export const RC_TRACTOR: WeaponDef = {
  id: 38,
  key: 'rc',
  name: 'RC Tractor',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 1,
  spread: 0,
  // Bots read it from here: they send the tractor off at something hostile this near.
  rangePx: 260,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 10,
  tractor: {
    hp: 45,
    speed: 70,
    accel: 34,
    maxSpeed: 300,
    turn: 3.2,
    ticks: ticks(8),
    gun: TRACTOR_GUN.id,
    every: 5,
    radius: 5,
    blast: 90,
    blastRadius: 44,
  },
  feel: { shake: 0.1, kick: 0, recoil: 0, bloom: 0, color: 0xf77622 },
};

/** The Monster Tonic's boulder (ADR-0102): torn out of the ground and bowled ahead, it rolls over everything in its way. */
export const BOULDER: WeaponDef = {
  id: 41,
  key: 'boulder',
  name: 'Boulder',
  rarity: 4,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  // 30 until ADR-0108.
  damage: 40,
  projectileSpeed: 190,
  spread: 0,
  rangePx: 170,
  pierce: 20,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 14,
  size: 12,
  knock: { speed: 170, ticks: 6, decay: 0.8, slam: true },
  feel: { shake: 0.35, kick: 4, recoil: 0, bloom: 0, color: 0x8b9bb4 },
};

/**
 * The Monster Tonic (ADR-0102): drunk, it turns its gardener into a hulking red monster for a while — a guard, a quicker
 * pace and a bigger body; the left button leaps onto the crosshair and crushes what is there (a Pumpkin King's slam), the
 * right one tears a boulder out of the ground and bowls it ahead.
 */
export const MONSTER: WeaponDef = {
  id: 40,
  key: 'monster',
  name: 'Monster Tonic',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  // One leap, then it gathers itself for the next (the magazine's bar fills back up).
  magazine: 1,
  reserve: INFINITE_RESERVE,
  // ADR-0108 (a boss's reward now, much stronger): 1.1 s, 34 a slam over 30 px, 150 px, a 90 HP guard, 30 a boulder
  // every 3.5 s until then.
  reloadTicks: ticks(0.8),
  damage: 46,
  // Bots lead a leap by this: the flight's mean pace.
  projectileSpeed: 330,
  spread: 0,
  rangePx: 170,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 0,
  leap: {
    ticks: ticks(0.45),
    min: 24,
    radius: 36,
    damage: 46,
    knock: { speed: 210, ticks: 6, decay: 0.78, slam: true },
  },
  guard: 140,
  walk: 1.15,
  hurt: 11,
  transform: true,
  time: ticks(22.5),
  alt: { weapon: BOULDER.id, shots: 1, cooldown: ticks(3), windup: ticks(0.45) },
  feel: { shake: 0.2, kick: 0, recoil: 0, bloom: 0, color: 0xe43b44 },
};

/**
 * A meteor (Zombie Nights' second stage, ADR-0186): nobody's weapon (a turret's, neutral), only what its blast hurts with
 * and the kill feed names; its numbers are `NIGHT_METEORS` (zombies.ts), its knock the blast's throw.
 */
export const METEOR: WeaponDef = {
  id: 42,
  key: 'meteor',
  name: 'Meteor',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 0,
  spread: 0,
  rangePx: 0,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 0,
  knock: { speed: 260, ticks: 7, decay: 0.8, slam: true },
  feel: { shake: 0.5, kick: 0, recoil: 0, bloom: 0, color: 0xf77622 },
};

/**
 * The Frost of a battle royale (ADR-0120): nobody's weapon (a turret's, neutral), only what it bites with outside the
 * circle and what the kill feed names; its numbers are `ROYALE.zone` (royale.ts).
 */
export const THE_FROST: WeaponDef = {
  id: 45,
  key: 'frost',
  name: 'The Frost',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 0,
  spread: 0,
  rangePx: 0,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 0,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0x9be8ff },
};

/**
 * A pit of lava (Zombie Nights' hell, ADR-0187, `HELL.lava`): no gun, the blow a gardener who falls in takes (the kill
 * feed's and the death recap's name for it), like the Frost's.
 */
export const THE_LAVA: WeaponDef = {
  id: 64,
  key: 'lava',
  name: 'Lava',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 0,
  spread: 0,
  rangePx: 0,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 0,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xf77622 },
};

/**
 * Chomper's blades (Zombie Nights, ADR-0186 amended): no gun, the blow that kills a gardener in its way (the kill feed's
 * and the death recap's name for it), like the lava's.
 */
export const THE_CHOMPER: WeaponDef = { ...THE_LAVA, id: 72, key: 'chomper', name: 'Chomper' };

/**
 * The casino's roulette (ADR-0232, `ROYALE.roulette`): no gun, what a spin that lands on red costs its spinner (the kill
 * feed's and the death recap's THE HOUSE), like the lava's.
 */
export const THE_HOUSE: WeaponDef = {
  ...THE_LAVA,
  id: 73,
  key: 'house',
  name: 'The House',
  feel: { ...THE_LAVA.feel, color: 0xe43b44 },
};

/**
 * The Fire Bow (ADR-0109): draw it (hold) and loose (let go) a flaming arrow that arcs over hedges and comes down on the
 * crosshair — as far as the draw allows — bursting in a little blast and leaving a patch of fire that burns whatever
 * walks through it.
 */
export const FIRE_BOW: WeaponDef = {
  id: 43,
  key: 'firebow',
  name: 'Fire Bow',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 30,
  splashDamage: 30,
  fireIntervalTicks: ticks(0.5),
  magazine: 4,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2.2),
  damage: 0,
  projectileSpeed: 300,
  spread: 0,
  rangePx: 300,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.65,
  muzzleDistance: 12,
  charge: { ticks: ticks(1), min: ticks(0.15), pierce: 0 },
  lob: { min: 60 },
  puddle: { radius: 22, ticks: ticks(2.5), impact: 0, damage: 2, every: 10 },
  burn: { ticks: ticks(1), damage: 2, every: 15 },
  feel: { shake: 0.18, kick: 2, recoil: 3, bloom: 4, color: 0xf77622 },
};

/**
 * The Shield (ADR-0109): hold the right button to stand behind it — a few blows and it breaks, growing back after a
 * while — or throw it with the left: it bounces off foes and fences and comes back to your hand.
 */
export const SHIELD: WeaponDef = {
  id: 44,
  key: 'shield',
  name: 'Shield',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(0.4),
  magazine: 3,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(7),
  damage: 24,
  projectileSpeed: 520,
  spread: 0,
  rangePx: 360,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 4,
  size: 6,
  knock: { speed: 140, ticks: 5, decay: 0.8, slam: true },
  shield: { arc: 1.2, grace: 2, walk: 0.6, bounces: 8, seek: 220, away: ticks(6), home: 1.2 },
  feel: { shake: 0.12, kick: 1.5, recoil: 3, bloom: 2, color: 0x8b9bb4 },
};

/** The Air Strike (ADR-0159): one use; draw a path on the map, a jet ripples rockets down it (JETS). */
export const AIR_STRIKE: WeaponDef = {
  id: 48,
  key: 'airstrike',
  name: 'Air Strike',
  rarity: 5,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 1,
  spread: 0,
  rangePx: 0,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 8,
  strike: JETS,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xf77622 },
};

/**
 * The Lightsaber (ADR-0159): quick swings of a blade of light in front of you; while you swing it, shots coming at you from
 * the front are cut out of the air. Neither swings nor parries use ammunition.
 */
export const LIGHTSABER: WeaponDef = {
  id: 49,
  key: 'saber',
  name: 'Laser Sword',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 9,
  // Endless like the Katana: neither swings nor parries spend power.
  magazine: 40,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.4),
  endless: true,
  damage: 30,
  projectileSpeed: 1,
  spread: 0,
  // The blade's reach from its holder's centre, for bots.
  rangePx: 34,
  pierce: 0,
  // One tick: the parry is up while it swings (`Gun.spin`).
  spinUpTicks: 1,
  firingMoveMultiplier: 1.1,
  muzzleDistance: 15,
  saw: { radius: 13, parry: 1.1, parryCost: 0.5 },
  // Thrown: out and home again in about a second, no cutting meanwhile.
  alt: { weapon: 58, shots: 1, cooldown: ticks(3), away: ticks(1) },
  feel: { shake: 0.1, kick: 0, recoil: -2, bloom: 2, color: 0x63c74d },
};

/**
 * The Death Scythe (ADR-0159): wide sweeps in front of you; every soul it cuts heals you, and what it cuts that is near
 * death dies.
 */
export const SCYTHE: WeaponDef = {
  id: 50,
  key: 'scythe',
  name: 'Death Scythe',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 14,
  magazine: 8,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.3),
  // 36 a sweep over 25 px, 6 back a hit, no execute until the user asked for more death in it.
  damage: 48,
  projectileSpeed: 1,
  spread: 0,
  rangePx: 50,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 15,
  saw: { radius: 30, parry: 0, parryCost: 0 },
  reap: 10,
  execute: 0.25,
  feel: { shake: 0.25, kick: 0, recoil: -3, bloom: 4, color: 0x8b9bb4 },
};

/** The Giant Hammer (ADR-0159): raised over the head and brought down in front, a stomp that throws everyone off their feet. */
export const HAMMER: WeaponDef = {
  id: 51,
  key: 'hammer',
  name: 'Giant Hammer',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 9,
  magazine: 4,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2),
  endless: true,
  damage: 70,
  projectileSpeed: 1,
  spread: 0,
  // Where its slam reaches, for bots.
  rangePx: 78,
  pierce: 0,
  // Raised from nothing before every slam.
  spinUpTicks: 11,
  firingMoveMultiplier: 1,
  muzzleDistance: 26,
  stomp: { radius: 52, knock: { speed: 290, ticks: 8, decay: 0.8, slam: true } },
  feel: { shake: 0.5, kick: 3, recoil: -4, bloom: 6, color: 0x8b9bb4 },
};

/**
 * Neptune's Trident (ADR-0159): one great wave, a tsunami like Hokusai's, rolling out wide through everything in its way
 * and carrying it off.
 */
export const TRIDENT: WeaponDef = {
  id: 52,
  key: 'trident',
  name: "Neptune's Trident",
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 20,
  magazine: 3,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.8),
  damage: 48,
  projectileSpeed: 300,
  spread: 0,
  rangePx: 260,
  // Through everything in its way.
  pierce: 30,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.8,
  muzzleDistance: 18,
  // A wall of water: what is within this of its line is swept up.
  size: 18,
  wave: true,
  knock: { speed: 230, ticks: 8, decay: 0.85, slam: true },
  feel: { shake: 0.3, kick: 3, recoil: 4, bloom: 6, color: 0x0099db },
};

/**
 * The Tank's guided missile (ADR-0159), on the right button: it bends hard onto the hostile thing it points at most nearly
 * and can see (the Homing Gun's `bend`), round corners too, and bursts on it.
 */
export const TANK_MISSILE: WeaponDef = {
  id: 54,
  key: 'tankMissile',
  name: 'Tank',
  rarity: 5,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: 38,
  splashDamage: 20,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 30,
  projectileSpeed: 300,
  spread: 0,
  rangePx: 460,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 12,
  bend: { turnPerPx: 0.03, range: 240, cone: 1.3 },
  feel: { shake: 0.35, kick: 3, recoil: 0, bloom: 5, color: 0xf77622 },
};

/**
 * The Tank (ADR-0159): you climb into a tank for a while — a thick guard, a slower pace; the left button fires its big
 * seeking shells (a better Bazooka), the right one a guided missile that bends onto what it sees.
 */
export const TANK: WeaponDef = {
  id: 53,
  key: 'tank',
  name: 'Tank',
  rarity: 5,
  turret: false,
  pellets: 1,
  splashRadius: 52,
  splashDamage: 25,
  fireIntervalTicks: ticks(1),
  magazine: 4,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(2.4),
  damage: 45,
  projectileSpeed: 460,
  spread: 0.02,
  rangePx: 380,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 22,
  seek: true,
  guard: 80,
  walk: 0.7,
  hurt: 13,
  transform: true,
  time: ticks(25),
  // A guided missile every 5 s.
  alt: { weapon: TANK_MISSILE.id, shots: 1, cooldown: ticks(5) },
  feel: { shake: 0.45, kick: 5, recoil: 5, bloom: 10, color: 0xf77622 },
};

/**
 * The Unicorn (ADR-0159): you turn into a unicorn for a while — quick on its hooves, a rainbow beam out of its horn, and on
 * the right button a fart that leaves a choking cloud behind it.
 */
export const UNICORN: WeaponDef = {
  id: 55,
  key: 'unicorn',
  name: 'Unicorn',
  rarity: 5,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 90,
  reserve: INFINITE_RESERVE,
  reloadTicks: ticks(1.5),
  damage: 3,
  projectileSpeed: 320 * 30,
  spread: 0,
  rangePx: 320,
  pierce: 2,
  spinUpTicks: 0,
  firingMoveMultiplier: 0.9,
  muzzleDistance: 14,
  beam: true,
  guard: 60,
  walk: 1.2,
  hurt: 10,
  transform: true,
  time: ticks(25),
  // A fart out behind: a cloud of gas that chokes whoever walks into it.
  alt: { weapon: 59, shots: 1, cooldown: ticks(5), behind: true },
  feel: { shake: 0.015, kick: 0.15, recoil: 0.5, bloom: 0.2, color: 0xf6757a },
};

/** The Lightsaber thrown (ADR-0159, its right button): it spins through two foes and on, then flies home. */
export const SABER_THROW: WeaponDef = {
  id: 58,
  key: 'saberThrow',
  name: 'Laser Sword',
  rarity: 4,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 40,
  projectileSpeed: 380,
  spread: 0,
  rangePx: 240,
  pierce: 2,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 12,
  size: 6,
  feel: { shake: 0.15, kick: 1.5, recoil: 0, bloom: 3, color: 0x63c74d },
};

/** The Unicorn's fart (ADR-0159): a cloud of gas set down behind it that chokes whoever stands in it. */
export const UNICORN_GAS: WeaponDef = {
  id: 59,
  key: 'gas',
  name: 'Unicorn',
  rarity: 5,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 150,
  spread: 0,
  // It drifts just out from under the tail.
  rangePx: 16,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 8,
  puddle: { radius: 30, ticks: ticks(4), impact: 0, damage: 4, every: 8 },
  burn: { ticks: ticks(1.5), damage: 2, every: 10 },
  feel: { shake: 0.2, kick: 0, recoil: 0, bloom: 0, color: 0x9be05a },
};

/** A duckling of the Duck Family (ADR-0159): small, quick to shoot down, a small bang. */
export const DUCKLING: WeaponDef = {
  id: 57,
  key: 'duckling',
  name: 'Duck Family',
  rarity: 4,
  turret: false,
  mount: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 1,
  spread: 0,
  rangePx: 0,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 0,
  tractor: {
    hp: 20,
    speed: 60,
    accel: 0,
    maxSpeed: 60,
    turn: 5,
    ticks: ticks(14),
    every: 0,
    radius: 3,
    blast: 40,
    blastRadius: 26,
    hunt: 170,
  },
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xfee761 },
};

/**
 * The Duck Family (ADR-0159): one use; a mother duck and her four ducklings waddle off and go for whatever hostile they
 * see, blowing up on it. Each can be shot down — it takes a few hits.
 */
export const DUCKS: WeaponDef = {
  id: 56,
  key: 'ducks',
  name: 'Duck Family',
  rarity: 4,
  turret: false,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 1,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 1,
  spread: 0,
  // Bots read it from here: they let the ducks go at something hostile this near.
  rangePx: 220,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 10,
  tractor: {
    hp: 45,
    speed: 60,
    accel: 0,
    maxSpeed: 60,
    turn: 4.5,
    ticks: ticks(14),
    every: 0,
    radius: 4,
    blast: 70,
    blastRadius: 38,
    hunt: 170,
    brood: { weapon: DUCKLING.id, count: 4 },
  },
  feel: { shake: 0.1, kick: 0, recoil: 0, bloom: 0, color: 0xfee761 },
};

/*
 * Zombie Nights' towers (ADR-0171): what each fires, fired by nothing else and never held (`turret`, in no crate, card or
 * armoury). Their numbers are the towers' as bought; a tower's tier sets its own (content `TOWERS`, `towerStats`).
 */

/**
 * A Sprinkler's spray: no projectile. Its `shot` from the tower marks a spray all round it, as far as the tower's reach by
 * its tier (content `towerReach`); the tower's system soaked and hurt the dead in it. Its `power` says which spray it was
 * (content `SprayKind`: a plain one, a geyser, one that freezes the soaked solid).
 */
export const SPRINKLER_SPRAY: WeaponDef = {
  id: 60,
  key: 'sprinkler',
  name: 'Sprinkler',
  rarity: 0,
  turret: true,
  mark: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(1),
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 7,
  // The water's, for the client's drops: nothing flies.
  projectileSpeed: 120,
  spread: 0,
  rangePx: 56,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 0,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0x2ce8f5 },
};

/**
 * What a Beehive's bees sting with: no projectile. Its `shot` from the hive marks a bee let out; the bee is a `bee`
 * entity of the hive's (BEE's flight), and its stings are the hive's `hit`s with this weapon.
 */
export const HIVE_BEE: WeaponDef = {
  id: 61,
  key: 'hivebee',
  name: 'Beehive',
  rarity: 0,
  turret: true,
  mark: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: ticks(1.2),
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 10,
  projectileSpeed: BEE.speed,
  spread: 0,
  rangePx: 150,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 4,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xfeae34 },
};

/**
 * A Sunflower's sunbeam: a beam as the Laser's (it crosses its range within a tick), a tick of it every `every` ticks of
 * the tower's, as far as its reach by its tier. It reaches crows and cuts through armour.
 */
export const SUN_RAY: WeaponDef = {
  id: 62,
  key: 'sunray',
  name: 'Sunflower',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 0,
  splashDamage: 0,
  fireIntervalTicks: 3,
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 4,
  projectileSpeed: 300 * 30,
  spread: 0,
  rangePx: 190,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  beam: true,
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xfee761 },
};

/**
 * A Pumpkin Mortar's pumpkin: lobbed over everything onto where one of the dead will be (as a Lobber's, its `power` its
 * flight), at least `lob.min` px off; it bursts there in `splashRadius` for `splashDamage`. A crow flies over it, a
 * Digger under it.
 */
export const MORTAR_SHELL: WeaponDef = {
  id: 63,
  key: 'mortar',
  name: 'Pumpkin Mortar',
  rarity: 0,
  turret: true,
  pellets: 1,
  splashRadius: 34,
  splashDamage: 45,
  fireIntervalTicks: ticks(2.6),
  magazine: 1,
  reserve: 0,
  reloadTicks: 1,
  damage: 0,
  projectileSpeed: 160,
  spread: 0,
  rangePx: 260,
  pierce: 0,
  spinUpTicks: 0,
  firingMoveMultiplier: 1,
  muzzleDistance: 6,
  lob: { min: 48 },
  feel: { shake: 0, kick: 0, recoil: 0, bloom: 0, color: 0xf77622 },
};

export const WEAPONS: readonly WeaponDef[] = [
  POLLINATOR,
  THORNFIRE,
  LONGSTEM,
  WHACKER,
  WEED_BURNER,
  ROOT_ROCKET,
  CANNON_SHOT,
  SEED_SPIT,
  TOWER_MISSILE,
  THORN,
  SUNBEAM,
  ATOM_BOMB,
  SPORE,
  FROSTBITE,
  CONE_LOBBER,
  CORN_RAIL,
  PRUNER,
  VINE_FIST,
  SPINE,
  FIREFLY,
  BEEHIVE,
  BUZZSAW,
  SHURIKEN,
  SLUDGE,
  SHOTGUN,
  TORNADO,
  ABDUCTOR,
  BERSERKER,
  POISON_DART,
  CUPID_BOW,
  DUBSTEP,
  FIREWORK,
  MECHA,
  GRIMOIRE,
  MECH_ROCKET,
  WAND,
  ZOMBIE_ACID,
  ALIEN_ATTACK,
  RC_TRACTOR,
  TRACTOR_GUN,
  MONSTER,
  BOULDER,
  METEOR,
  FIRE_BOW,
  SHIELD,
  THE_FROST,
  ALTAR_BOLT,
  ROTTEN_GOURD,
  AIR_STRIKE,
  LIGHTSABER,
  SCYTHE,
  HAMMER,
  TRIDENT,
  TANK,
  TANK_MISSILE,
  UNICORN,
  DUCKS,
  DUCKLING,
  SABER_THROW,
  UNICORN_GAS,
  SPRINKLER_SPRAY,
  HIVE_BEE,
  SUN_RAY,
  MORTAR_SHELL,
  THE_LAVA,
  ROYALE_PISTOL,
  GRENADE_THROW,
  C4_CHARGE,
  ROYALE_MINIGUN,
  ROYALE_HOMING,
  ROYALE_SNIPER,
  EJECTOR_BLAST,
  THE_CHOMPER,
  THE_HOUSE,
];
/**
 * How hard a human's shot bends onto a hostile target it is about to miss (ADR-0016). Targets count when they are
 * ahead of it, within `range` px, and its straight line would pass them by at most `width` px beyond their hurt radius;
 * the one it misses by least wins. The shot then turns towards that target's centre by at most `turnPerPx` radians per
 * px flown (a constant curvature, the same on the server and in the client's visual bullet whatever the frame rate).
 * A beam crosses its whole range in one tick, so for it this is a snap onto a target near the line.
 */
export interface AimHelp {
  readonly turnPerPx: number;
  readonly width: number;
  readonly range: number;
}

/**
 * How much help a match gives humans' shots (ADR-0026, ADR-0054): `standard` between players (and in the range),
 * `extra` against the dead (Zombie Nights). Bots never get any.
 */
export const ASSIST_LEVELS = ['standard', 'extra'] as const;
export type AssistLevel = (typeof ASSIST_LEVELS)[number];

/**
 * Aim help per level (ADR-0026). Humans only: bots' shots, turrets, boss seeds and thorns fly straight. `bullet` is
 * for bullets and beams (the Laser); `seek` for the Bazooka's rockets. Flames and grenades (their fuse is
 * the help) fly as they are.
 * - standard: at 150 px a bullet makes up ~12 px (ADR-0029 raised it from 0.0008 / 9 px / 240 px, ~9 px; rockets
 *   from 0.0042 / 40 / 330). Arena (ADR-0016): 0.0008 / 8 px on bots meant +60% player hits, so this stays for
 *   humans.
 * - extra: halfway between (ADR-0054: one level for everyone against the bots, where EASY and HARD had strong and
 *   standard) — bullets make up ~20 px over 150 px and catch targets 16 px off their line.
 */
export const AIM_ASSIST: Record<AssistLevel, { readonly bullet: AimHelp; readonly seek: AimHelp }> = {
  standard: {
    bullet: { turnPerPx: 0.0011, width: 12, range: 250 },
    seek: { turnPerPx: 0.0048, width: 46, range: 340 },
  },
  extra: {
    bullet: { turnPerPx: 0.00175, width: 16, range: 265 },
    seek: { turnPerPx: 0.00565, width: 51, range: 350 },
  },
};

/** The `kill` event's weapon for a blow no weapon dealt (a minion's swing, a boss's slam or bite, a creep's lunge). */
export const NO_WEAPON = 0xff;

/** Weapons a player can hold. */
export const PLAYER_WEAPONS: readonly WeaponDef[] = WEAPONS.filter((w) => !w.turret && !w.mount);
export const DEFAULT_WEAPON = POLLINATOR;

/** Is `weapon` (a content weapon id) a Pistol: the default gun, or a battle royale's (ADR-0189)? Its skins go on it. */
export const isPistol = (weapon: number): boolean => weaponById(weapon).key === DEFAULT_WEAPON.key;

/** Extra targets a shot of `weapon` goes through: its `royalePierce` in a battle royale, else its `pierce`. */
export const pierceOf = (weapon: WeaponDef, royale: boolean): number =>
  royale ? (weapon.royalePierce ?? weapon.pierce) : weapon.pierce;

/**
 * A battle royale's own guns (ADR-0189), by the gun each stands in for there: its Pistol, Sniper, Minigun and Homing
 * Gun.
 */
const ROYALE_GUNS: Readonly<Partial<Record<number, WeaponDef>>> = {
  [POLLINATOR.id]: ROYALE_PISTOL,
  [LONGSTEM.id]: ROYALE_SNIPER,
  [WHACKER.id]: ROYALE_MINIGUN,
  [FIREFLY.id]: ROYALE_HOMING,
};

/** The gun a gardener given `weapon` holds: in a battle royale its own (`ROYALE_GUNS`), where it has one. */
export const royaleGun = (weapon: WeaponDef, royale: boolean): WeaponDef =>
  (royale ? ROYALE_GUNS[weapon.id] : undefined) ?? weapon;

/** The gun `weapon` (a content id) stands in for, `royaleGun`'s other way (its loot's: ADR-0189); any other, itself. */
export const baseGun = (weapon: number): number =>
  Number(Object.keys(ROYALE_GUNS).find((k) => ROYALE_GUNS[Number(k)]?.id === weapon) ?? weapon);

/** Bullet ids one shot may use: a weapon fires at most this many pellets. */
export const SHOTS_PER_SEQ = 8;

/**
 * Id of the `pellet`-th bullet of the shot fired with input `seq` (each bullet needs its own id: hits and
 * wall ends reference it). Turrets use their own shot counter as `seq`.
 */
export const shotId = (seq: number, pellet = 0): number => seq * SHOTS_PER_SEQ + pellet;

/** The input seq (a turret's shot count) bullet `id` was fired with: `shotId`'s inverse. */
export const shotIndex = (id: number): number => Math.floor(id / SHOTS_PER_SEQ);

/** Which bullet of its shot `id` is (0: the first — one flash and one sound a trigger pull). */
export const pelletOf = (id: number): number => id % SHOTS_PER_SEQ;

/** A Snapdragon's seeds are numbered attack × BOSS_VOLLEY + seed (never 0: that means melee); a volley is < this. */
export const BOSS_VOLLEY = 32;

/** A lob's `power` counts its flight in steps of this many px (ADR-0109): a u8 reaches 510 px. */
export const LOB_STEP = 2;

/** How far a lob (ADR-0109) of `power` flies (px, from the muzzle) and in how many ticks. */
export function lobFlight(weapon: WeaponDef, power: number): { px: number; ticks: number } {
  const px = power * LOB_STEP;
  return { px, ticks: Math.max(4, Math.ceil((px / weapon.projectileSpeed) * TICK_RATE)) };
}

export function flightPx(weapon: WeaponDef, shot: number): number {
  if (!weapon.stagger || weapon.pellets < 2) return weapon.rangePx;
  const n = weapon.pellets;
  const k = (pelletOf(shot) * 2 + shotIndex(shot)) % n;
  return weapon.rangePx * (1 - (weapon.stagger * k) / (n - 1));
}

export function weaponById(id: number): WeaponDef {
  const w = WEAPONS[id];
  if (!w) throw new Error(`Unknown weapon id ${id}`);
  return w;
}
